import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../supabase'
import PlanDelDia from './PlanDelDia'
import WeekPrintReport from './WeekPrintReport'
import PlanMultiDayPrint from './PlanMultiDayPrint'
import { syncJobCreate, syncJobUpdate, syncJobDelete, connectGoogleCalendar, disconnectGoogleCalendar, isConnected, bulkSyncJobs } from '../googleCalendar'

const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']
const DAYS_ES = { Mon:'Lunes', Tue:'Martes', Wed:'Miércoles', Thu:'Jueves', Fri:'Viernes', Sat:'Sábado', Sun:'Domingo' }
const DAY_BADGE = { Mon:'L', Tue:'M', Wed:'Mi', Thu:'J', Fri:'V' }

const FREQ_OPTIONS = [
  { v:'weekly',     l:'Semanal (cada semana)',        weeks:1  },
  { v:'biweekly',   l:'Cada 2 semanas',               weeks:2  },
  { v:'triweekly',  l:'Cada 3 semanas',               weeks:3  },
  { v:'fortweekly', l:'Cada 4 semanas',               weeks:4  },
  { v:'monthly',    l:'Mensual',                      weeks:4  },
  { v:'bimonthly',  l:'Bimestral (cada 2 meses)',     weeks:8  },
  { v:'custom',     l:'Personalizado',                weeks:null},
  { v:'one-time',   l:'Una sola vez',                 weeks:null},
]

const START_HOUR = 4
const END_HOUR = 24
const SLOT_HEIGHT = 40
const TOTAL_SLOTS = (END_HOUR - START_HOUR) * 2

function fmt12(h, m) {
  if (h === 24) return `12:${String(m).padStart(2,'0')} AM`
  if (h === 0) return `12:${String(m).padStart(2,'0')} AM`
  const ampm = h >= 12 ? 'PM' : 'AM'
  const hh = h % 12 || 12
  return `${hh}:${String(m).padStart(2,'0')} ${ampm}`
}

function timeStrToMin(t) {
  if (!t) return 0
  const [hm, ap] = t.split(' ')
  const [h, m] = hm.split(':').map(Number)
  return ((h % 12) + (ap === 'PM' ? 12 : 0)) * 60 + m
}

function minToTimeStr(min) {
  const totalMin = min + START_HOUR * 60
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  return fmt12(h, m)
}

function minToTop(min) { return (min / 30) * SLOT_HEIGHT }
function hrsToHeight(hrs) { return (hrs * 60 / 30) * SLOT_HEIGHT }

function getWeekDates(offset) {
  const now = new Date()
  const dow = now.getDay()
  const mon = new Date(now)
  mon.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1) + offset * 7)
  return DAYS.map((_, i) => { const d = new Date(mon); d.setDate(mon.getDate() + i); return d })
}

// Supabase returns at most 1000 rows per request. Page through so the
// due/overdue math sees every job, not just the first 1000.
async function fetchAllRows(makeQuery) {
  const all = []
  for (let from = 0; ; from += 1000) {
    const { data, error } = await makeQuery().range(from, from + 999)
    if (error) return { data: all, error }
    all.push(...(data || []))
    if (!data || data.length < 1000) break
  }
  return { data: all }
}

function dateKey(d) {
  return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0')
}
function wkKey(o) { return 'w' + o }

// ── Recurring due / overdue logic ─────────────────────────────────────────────
// Works on plain YYYY-MM-DD strings so the time of day never matters
// (the old version compared against "Monday at the current clock time", so any
// client due on a Monday showed as overdue once it was past noon on Monday).
function dkAddDays(dk, n) {
  const d = new Date(dk + 'T12:00:00'); d.setDate(d.getDate() + n); return dateKey(d)
}
function dkDiffDays(a, b) { // a - b in days
  return Math.round((new Date(a + 'T12:00:00') - new Date(b + 'T12:00:00')) / 86400000)
}
// Real visits only: skipped, overdue-marked and estimate rows don't count as a cleaning
const NOT_A_VISIT = ['skipped', 'overdue', 'estimate']

// Returns 'due' if the client's next cleaning falls in this week,
// 'overdue' if it was already missed (before the real current week), or null.
function recurringStatus(client, allJobs, weekDates) {
  const freq = FREQ_OPTIONS.find(f => f.v === client.frequency)
  if (!freq || !freq.weeks) return null
  const every = freq.weeks * 7
  const weekStart = dateKey(weekDates[0])
  const weekEnd = dkAddDays(weekStart, 6)
  const realWeekStart = dateKey(getWeekDates(0)[0])

  const visits = allJobs
    .filter(j => j.client_id === client.id && !NOT_A_VISIT.includes(j.status))
    .map(j => j.scheduled_date).filter(Boolean).sort()

  // Manually moved to Overdue and not rebooked since → overdue
  const lastManualOverdue = allJobs.filter(j => j.client_id === client.id && j.status === 'overdue')
    .map(j => j.scheduled_date).sort().pop()
  if (lastManualOverdue && !visits.some(v => v >= lastManualOverdue)) return 'overdue'

  const lastBefore = [...visits].filter(v => v < weekStart).pop() || null

  // A manually set next date only counts if nobody has been cleaned in the
  // stretch it was meant to cover (otherwise it's a leftover that shifts the rhythm)
  let next = null
  if (client.next_date && !visits.some(v => v >= dkAddDays(client.next_date, -every)) && (!lastBefore || client.next_date > lastBefore)) {
    next = client.next_date
  } else if (lastBefore) {
    next = dkAddDays(lastBefore, every)
  }
  if (!next) return null

  // Looking at a future week: assume the rhythm keeps going
  while (next < weekStart && next >= realWeekStart) next = dkAddDays(next, every)

  // Already booked later than this week but before the cleaning after it → it was moved, not missed
  if (visits.some(v => v > weekEnd && dkDiffDays(v, next) < every)) return null

  if (next >= weekStart && next <= weekEnd) return 'due'
  if (next < weekStart) return 'overdue'
  return null
}

// Kept for callers that only need "is this client due this week"
function isClientDueThisWeek(client, weekDates, allJobs) {
  if (client.frequency === 'one-time' || client.frequency === 'custom') return false
  return recurringStatus(client, allJobs, weekDates) === 'due'
}

const COLORS = [
  { bg:'#dbeafe', border:'#3b82f6', text:'#1e40af' },
  { bg:'#dcfce7', border:'#22c55e', text:'#166534' },
  { bg:'#ede9fe', border:'#a78bfa', text:'#5b21b6' },
  { bg:'#fef3c7', border:'#f59e0b', text:'#92400e' },
  { bg:'#fce7f3', border:'#ec4899', text:'#9d174d' },
]

export default function Schedule({ onNavigate }) {
  const [weekOffset, setWeekOffset] = useState(0)
  const [dayOffset, setDayOffset] = useState(0)
  const [viewMode, setViewMode] = useState('week') // 'week' | '3day' | 'month' | 'mass'
  const allWeekDates = getWeekDates(weekOffset)
  const dates = viewMode === '3day' ? allWeekDates.slice(dayOffset, dayOffset+3) : allWeekDates
  const [clients, setClients] = useState([])
  const [staff, setStaff] = useState([])
  const [jobs, setJobs] = useState([])
  const [allJobs, setAllJobs] = useState([])
  const [notepad, setNotepad] = useState('')
  const [dragging, setDragging] = useState(null)
  const [dragOver, setDragOver] = useState(null)
  const [editJob, setEditJob] = useState(null)
  const [showAdd, setShowAdd] = useState(false)
  const [showEstimate, setShowEstimate] = useState(false)
  const [showPlan, setShowPlan] = useState(null) // date string or null
  const [showWeekSummary, setShowWeekSummary] = useState(false)
  const [weekSummaryData, setWeekSummaryData] = useState(null)
  const [routeStaffMap, setRouteStaffMap] = useState({})
  const [timesheetRows, setTimesheetRows] = useState([])
  const [showPrintReport, setShowPrintReport] = useState(false)
  const [showDayPicker, setShowDayPicker] = useState(false)
  const [pickedDays, setPickedDays] = useState(new Set())
  const [showMultiDayPrint, setShowMultiDayPrint] = useState(false)
  const [sbTab, setSbTab] = useState('week') // week | overdue | floating
  const [addData, setAddData] = useState({ clientId:'', day:'Mon', time:'9:00 AM' })
  const [estimateData, setEstimateData] = useState({ address:'', day:'Mon', time:'9:00 AM', notes:'' })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [gcalConnected, setGcalConnected] = useState(isConnected())

  const wk = wkKey(weekOffset)

  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  useEffect(() => {
    const handler = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  const fetchWeek = useCallback(async () => {
    setLoading(true)
    const monStr = dateKey(dates[0])
    const friStr = dateKey(dates[dates.length-1])

    // Get 6 months of job history for next-date calculations
    const sixMonthsAgo = new Date()
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)

    const [{ data: cl }, { data: sf }, { data: jb }, { data: hist }, { data: wn }, { data: cleanHist }] = await Promise.all([
      supabase.from('clients').select('*').eq('is_active', true).order('name'),
      supabase.from('staff').select('*').eq('is_active', true).order('name'),
      supabase.from('jobs').select('*').gte('scheduled_date', monStr).lte('scheduled_date', friStr),
      fetchAllRows(() => supabase.from('jobs').select('id,client_id,scheduled_date,status').gte('scheduled_date', dateKey(sixMonthsAgo)).order('scheduled_date').order('id')),
      supabase.from('week_notes').select('*').eq('week_key', wk).maybeSingle(),
      fetchAllRows(() => supabase.from('cleaning_history').select('id,client_id,cleaned_date').gte('cleaned_date', dateKey(sixMonthsAgo)).order('cleaned_date').order('id')),
    ])

    setClients(cl || [])
    setStaff(sf || [])
    setJobs(jb || [])
    // Merge jobs + cleaning_history so we have complete last-seen data
    const jobEntries = (hist||[]).map(j=>({ client_id: j.client_id, scheduled_date: j.scheduled_date, status: j.status }))
    const histEntries = (cleanHist||[]).map(h=>({ client_id: h.client_id, scheduled_date: h.cleaned_date }))
    setAllJobs([...jobEntries, ...histEntries])
    setNotepad(wn?.note || '')
    setLoading(false)
  }, [weekOffset])

  useEffect(() => { fetchWeek() }, [fetchWeek])

  async function openWeekSummary() {
    setShowWeekSummary(true)
    setWeekSummaryData(null)
    const monStr = dateKey(dates[0])
    const friStr = dateKey(dates[dates.length-1])
    // Fetch routes for the week
    const { data: routes } = await supabase.from('routes').select('*').gte('route_date', monStr).lte('route_date', friStr)
    const { data: weekJobs } = await supabase.from('jobs').select('*').gte('scheduled_date', monStr).lte('scheduled_date', friStr).eq('status', 'scheduled')
    // Build summary: per staff member, which days and total hours
    const summary = {}
    staff.forEach(s => { summary[s.id] = { name: s.name, days: {}, totalHours: 0 } })
    const byRouteDay = {}
    ;(routes||[]).filter(r=>r.label!=='__draft__').forEach(r => {
      let staffIds = []
      try { staffIds = JSON.parse(r.notes||'{}').staffIds || [] } catch(e) {}
      byRouteDay[r.route_date + '|' + r.label] = staffIds
      const dayJobs = (weekJobs||[]).filter(j=>j.scheduled_date===r.route_date && j.route===r.label)
      const dayHours = dayJobs.reduce((sum,j)=>sum+(parseFloat(j.hours)||0),0)
      staffIds.forEach(sid => {
        if (!summary[sid]) return
        summary[sid].days[r.route_date] = (summary[sid].days[r.route_date]||0) + dayHours
        summary[sid].totalHours += dayHours
      })
    })
    setRouteStaffMap(byRouteDay)

    // Fetch actual clocked time (from staff Clock In/Out) for this week's jobs
    const weekJobIds = (weekJobs||[]).map(j=>j.id)
    const timesheetRows = []
    if (weekJobIds.length) {
      const { data: logs } = await supabase.from('time_logs').select('*').in('job_id', weekJobIds)
      const jobById = {}
      ;(weekJobs||[]).forEach(j => { jobById[j.id] = j })

      // Group actual clock logs by job so we know, per job, exactly which staffIds
      // logged their own time — no copying times between teammates
      const logsByJob = {}
      ;(logs||[]).forEach(log => {
        if (!log.clock_in || !log.clock_out) return
        ;(logsByJob[log.job_id] = logsByJob[log.job_id]||[]).push(log)
      })

      ;(weekJobs||[]).forEach(job => {
        const cl = clients.find(c=>c.id===job.client_id)
        const clientName = cl?.nickname || cl?.name || '?'
        const routeDayKey = job.scheduled_date + '|' + job.route
        const jobLogs = logsByJob[job.id] || []
        const assignedStaffIds = byRouteDay[routeDayKey]?.length ? byRouteDay[routeDayKey] : jobLogs.map(l=>l.staff_id)

        assignedStaffIds.forEach(sid => {
          const entry = summary[sid]
          if (!entry) return
          const ownLog = jobLogs.find(l=>l.staff_id===sid)

          if (ownLog) {
            const hrs = (new Date(ownLog.clock_out) - new Date(ownLog.clock_in)) / 3600000
            entry.actualDays = entry.actualDays || {}
            entry.actualDays[job.scheduled_date] = (entry.actualDays[job.scheduled_date]||0) + hrs
            entry.actualTotal = (entry.actualTotal||0) + hrs
            const staffMember = staff.find(st=>st.id===sid)
            timesheetRows.push({
              staffId: sid,
              staffName: staffMember?.name || entry.name || 'Desconocido',
              route: job.route || '—',
              date: job.scheduled_date,
              clientName,
              clockIn: ownLog.clock_in,
              clockOut: ownLog.clock_out,
              hours: hrs,
            })
          } else {
            // This specific person has no clock record for this job — flag it,
            // even if a teammate on the same route/job did clock in/out
            const staffMember = staff.find(st=>st.id===sid)
            timesheetRows.push({
              staffId: sid,
              staffName: staffMember?.name || entry.name || 'Desconocido',
              route: job.route || '—',
              date: job.scheduled_date,
              clientName,
              clockIn: null,
              clockOut: null,
              hours: null,
              noLog: true,
            })
          }
        })
      })
    }
    setTimesheetRows(timesheetRows)

    setWeekSummaryData(Object.values(summary).filter(s=>s.totalHours>0).sort((a,b)=>b.totalHours-a.totalHours))
  }

  useEffect(() => {
    const t = setTimeout(async () => {
      await supabase.from('week_notes').upsert(
        { week_key: wk, note: notepad, updated_at: new Date().toISOString() },
        { onConflict: 'week_key' }
      )
    }, 800)
    return () => clearTimeout(t)
  }, [notepad])

  // Sidebar: exclude realtors, inactive, airbnb — split into "this week" and "floating"
  const scheduledClientIds = new Set(jobs.filter(j => j.status !== 'overdue').map(j => j.client_id))
  const sidebarEligible = clients.filter(c => {
    if (!c.is_active) return false
    if (c.is_realtor) return false
    if (c.is_airbnb) return false
    if (c.is_commercial) return false
    return true
  })

  // Due this week — only clients whose next date falls in this week
  const dueClients = sidebarEligible.filter(c => {
    if (scheduledClientIds.has(c.id)) return false
    return isClientDueThisWeek(c, allWeekDates, allJobs)
  })

  // Overdue — already missed (see recurringStatus)
  const overdueClients = sidebarEligible.filter(c => {
    if (scheduledClientIds.has(c.id)) return false
    if (dueClients.find(d => d.id === c.id)) return false
    if (['custom','one-time'].includes(c.frequency)) return false
    return recurringStatus(c, allJobs, allWeekDates) === 'overdue'
  })

  // Floating — custom/one-time not yet scheduled
  const floatingClients = sidebarEligible.filter(c => {
    if (scheduledClientIds.has(c.id)) return false
    if (dueClients.find(d => d.id === c.id)) return false
    if (overdueClients.find(d => d.id === c.id)) return false
    return c.frequency === 'custom' || c.frequency === 'one-time'
  })

  // Skipped jobs no longer shown in a dedicated tab, but skipJob() still works

  function displayName(client) {
    return client?.nickname || client?.name || 'Cliente inactivo'
  }

  function jobsForDate(date) {
    return jobs.filter(j => j.scheduled_date === dateKey(date))
  }

  // Compute side-by-side columns for overlapping jobs (Google Calendar style)
  function computeColumns(dayJobs) {
    const timed = dayJobs.filter(j => j.start_time && j.status !== 'estimate' && j.status !== 'skipped' && j.status !== 'overdue')
    const allDay = dayJobs.filter(j => !j.start_time && j.status !== 'estimate' && j.status !== 'skipped' && j.status !== 'overdue')
    
    // Sort by start time
    const sorted = [...timed].sort((a,b) => timeStrToMin(a.start_time||'9:00 AM') - timeStrToMin(b.start_time||'9:00 AM'))
    
    const cols = [] // array of columns, each column is array of job ids
    const jobCol = {} // jobId -> column index
    
    sorted.forEach(job => {
      const js = timeStrToMin(job.start_time) - START_HOUR*60
      const je = js + (parseFloat(job.hours)||2)*60
      // Find first column where this job doesn't overlap
      let placed = false
      for (let ci = 0; ci < cols.length; ci++) {
        const colJobs = cols[ci].map(id => timed.find(j=>j.id===id))
        const overlaps = colJobs.some(cj => {
          if (!cj) return false
          const cs = timeStrToMin(cj.start_time) - START_HOUR*60
          const ce = cs + (parseFloat(cj.hours)||2)*60
          return js < ce && je > cs
        })
        if (!overlaps) {
          cols[ci].push(job.id)
          jobCol[job.id] = ci
          placed = true
          break
        }
      }
      if (!placed) {
        cols.push([job.id])
        jobCol[job.id] = cols.length - 1
      }
    })
    
    // For each job, determine how many columns span its time range
    const jobSpan = {}
    sorted.forEach(job => {
      const js = timeStrToMin(job.start_time) - START_HOUR*60
      const je = js + (parseFloat(job.hours)||2)*60
      let maxCol = jobCol[job.id]
      sorted.forEach(other => {
        if (other.id === job.id) return
        const os = timeStrToMin(other.start_time) - START_HOUR*60
        const oe = os + (parseFloat(other.hours)||2)*60
        if (js < oe && je > os) maxCol = Math.max(maxCol, jobCol[other.id])
      })
      jobSpan[job.id] = maxCol + 1
    })
    
    return { jobCol, jobSpan, totalCols: cols.length, allDay }
  }

  function hasConflict(job, dayJobs) {
    if (!job.start_time || !job.hours) return false
    const js = timeStrToMin(job.start_time) - START_HOUR * 60
    const je = js + job.hours * 60
    return dayJobs.filter(j => j.id !== job.id).some(j => {
      if (!j.start_time) return false
      if (job.route && j.route && job.route !== j.route) return false
      const ks = timeStrToMin(j.start_time) - START_HOUR * 60
      const ke = ks + (j.hours || 2) * 60
      return js < ke && je > ks
    })
  }

  function getMinFromY(e, col) {
    const rect = col.getBoundingClientRect()
    const y = e.clientY - rect.top
    const rawMin = (y / SLOT_HEIGHT) * 30
    return Math.max(0, Math.min(Math.round(rawMin / 30) * 30, (END_HOUR - START_HOUR) * 60 - 30))
  }

  async function dropJobAllDay(date, clientId) {
    const client = clients.find(c => c.id === clientId)
    if (!client) return
    setSaving(true)
    const { data } = await supabase.from('jobs').insert([{
      client_id: clientId,
      scheduled_date: dateKey(date),
      start_time: null,
      hours: client.default_hours || 2,
      status: 'scheduled',
      route: '', one_time_note: '',
      week_key: wk,
      color_idx: jobs.length % 5,
    }]).select().single()
    if (data) {
      setJobs(prev => [...prev, data])
      syncJobCreate(data, client)
    }
    setSaving(false)
  }

  async function moveJobAllDay(jobId, newDate) {
    const { data } = await supabase.from('jobs')
      .update({ scheduled_date: dateKey(newDate), start_time: null })
      .eq('id', jobId).select().single()
    if (data) {
      setJobs(prev => prev.map(j => j.id === jobId ? data : j))
      const client = clients.find(c => c.id === data.client_id)
      syncJobUpdate(data, client || {})
    }
  }

  async function dropJob(date, clientId, startMin) {
    const client = clients.find(c => c.id === clientId)
    if (!client) return
    setSaving(true)
    const { data } = await supabase.from('jobs').insert([{
      client_id: clientId,
      scheduled_date: dateKey(date),
      start_time: minToTimeStr(startMin),
      hours: client.default_hours || 2,
      status: 'scheduled',
      route: '', one_time_note: '',
      week_key: wk,
      color_idx: jobs.length % 5,
    }]).select().single()
    if (data) {
      setJobs(prev => [...prev, data])
      syncJobCreate(data, client)
    }
    setSaving(false)
  }

  async function moveJob(jobId, newDate, newStartMin) {
    const { data } = await supabase.from('jobs')
      .update({ scheduled_date: dateKey(newDate), start_time: minToTimeStr(newStartMin) })
      .eq('id', jobId).select().single()
    if (data) {
      setJobs(prev => prev.map(j => j.id === jobId ? data : j))
      const client = clients.find(c => c.id === data.client_id)
      syncJobUpdate(data, client || {})
    }
  }

  async function dismissOverdue(client) {
    setSaving(true)
    // Clear any jobs manually marking this client overdue
    const { data: overdueJobs } = await supabase
      .from('jobs')
      .select('id')
      .eq('client_id', client.id)
      .eq('status', 'overdue')
    if (overdueJobs?.length) {
      await supabase.from('jobs').delete().in('id', overdueJobs.map(j => j.id))
    }
    // If they're ALSO overdue automatically (recurring schedule says so),
    // push their next date forward so they don't reappear immediately
    const freq = FREQ_OPTIONS.find(f => f.v === client.frequency)
    if (freq?.weeks) {
      const next = new Date()
      next.setDate(next.getDate() + freq.weeks * 7)
      await supabase.from('clients').update({ next_date: next.toISOString().split('T')[0] }).eq('id', client.id)
    }
    setSaving(false)
    fetchWeek()
  }

  async function moveToOverdue(job) {
    if (!job) return
    setSaving(true)
    // Mark job as force_overdue — removes from calendar, client appears in Overdue sidebar
    const { data, error } = await supabase.from('jobs').update({ status: 'overdue' }).eq('id', job.id).select()
    if (error) {
      console.error('moveToOverdue update failed:', error)
      alert('No se pudo mover a Overdue: ' + error.message)
      setSaving(false)
      return
    }
    if (!data || data.length === 0) {
      console.error('moveToOverdue: no row updated for job id', job.id)
      alert('No se encontró el trabajo para actualizar. Revisa la consola.')
      setSaving(false)
      return
    }
    syncJobDelete(job.id)
    setEditJob(null)
    setSaving(false)
    fetchWeek()
  }

  async function skipJob(job, returnDate) {
    if (!job || !returnDate) { alert('Por favor selecciona una fecha de regreso'); return }
    setSaving(true)
    const client = clients.find(c => c.id === job.client_id)

    // 1. Mark this job as skipped
    await supabase.from('jobs').update({ status: 'skipped' }).eq('id', job.id)
    syncJobDelete(job.id)

    // 2. Find the ONE next scheduled job for this client after today
    const { data: nextJobs } = await supabase
      .from('jobs')
      .select('*')
      .eq('client_id', job.client_id)
      .eq('status', 'scheduled')
      .gt('scheduled_date', job.scheduled_date)
      .order('scheduled_date', { ascending: true })
      .limit(1)

    // 3. Shift that next job forward 7 days
    if (nextJobs?.length) {
      const next = nextJobs[0]
      const d = new Date(next.scheduled_date + 'T12:00:00')
      d.setDate(d.getDate() + 7)
      const newDate = d.toISOString().split('T')[0]
      const { data: updated } = await supabase
        .from('jobs')
        .update({ scheduled_date: newDate, start_time: null })
        .eq('id', next.id)
        .select().single()
      if (updated && client) syncJobUpdate(updated, client)
    }

    // 4. Create new job on return date (no time)
    const { data: newJob } = await supabase.from('jobs').insert([{
      client_id: job.client_id,
      scheduled_date: returnDate,
      start_time: null,
      hours: job.hours || 2,
      status: 'scheduled',
      route: job.route || '',
      one_time_note: job.one_time_note || '',
      week_key: returnDate.slice(0, 7),
      color_idx: job.color_idx || 0,
    }]).select().single()
    if (newJob && client) {
      syncJobCreate(newJob, client)
      // Mark the original as resolved so it drops off the Skipped tab
      await supabase.from('jobs').update({ skip_resolved: true }).eq('id', job.id)
    }

    setEditJob(null)
    setSaving(false)
    fetchWeek()
  }

  async function removeJob(jobId) {
    await supabase.from('jobs').delete().eq('id', jobId)
    syncJobDelete(jobId)
    setJobs(prev => prev.filter(j => j.id !== jobId))
    setEditJob(null)
  }

  async function saveEdit() {
    if (!editJob) return
    setSaving(true)
    const { data } = await supabase.from('jobs').update({
      start_time: editJob.start_time,
      hours: parseFloat(editJob.hours),
      route: editJob.route || '',
      one_time_note: editJob.one_time_note || '',
      one_time_address: editJob.one_time_address || '',
      not_same_day: editJob.not_same_day || false,
      status: editJob.status || 'scheduled',
      skip_return_date: editJob.skip_return_date || null,
    }).eq('id', editJob.id).select().single()
    if (data) {
      setJobs(prev => prev.map(j => j.id === editJob.id ? data : j))
      const client = clients.find(c => c.id === data.client_id)
      syncJobUpdate(data, client || {})
    }
    setEditJob(null)
    setSaving(false)
  }

  async function addCleaning() {
    const client = clients.find(c => c.id === addData.clientId)
    if (!client) return
    const di = DAYS.indexOf(addData.day)
    if (di < 0) return
    setSaving(true)
    const { data } = await supabase.from('jobs').insert([{
      client_id: addData.clientId,
      scheduled_date: dateKey(dates[di]),
      start_time: addData.time,
      hours: client.default_hours || 2,
      status: 'scheduled',
      route: '', one_time_note: '',
      week_key: wk, color_idx: jobs.length % 5,
    }]).select().single()
    if (data) {
      setJobs(prev => [...prev, data])
      syncJobCreate(data, client)
    }
    setShowAdd(false)
    setSaving(false)
  }

  const lastDate = dates[dates.length-1]
  const weekLabel = `${dates[0].toLocaleDateString('es-MX',{month:'short',day:'numeric'})} – ${lastDate.toLocaleDateString('es-MX',{month:'short',day:'numeric',year:'numeric'})}`
  const isMobile = windowWidth <= 768

  // ── MOBILE VIEW ────────────────────────────────────────────────────────────
  if (isMobile) return <MobileSchedule
    clients={clients} jobs={jobs} allJobs={allJobs} dates={allWeekDates}
    weekOffset={weekOffset} setWeekOffset={setWeekOffset}
    loading={loading} staff={staff} wk={wk}
    onAddJob={async (clientId, date, timeStr, hours, route, staffIds, note) => {
      const client = clients.find(c => c.id === clientId)
      if (!client) return
      const { data } = await supabase.from('jobs').insert([{
        client_id: clientId, scheduled_date: dateKey(date),
        start_time: timeStr || null,
        hours: hours || client.default_hours || 2,
        route: route || '',
        status: 'scheduled',
        one_time_note: note || '',
        week_key: wk, color_idx: jobs.length % 5,
      }]).select().single()
      if (data) {
        setJobs(prev => [...prev, data])
        syncJobCreate(data, client)
        if (staffIds?.length) {
          await supabase.from('job_staff').insert(staffIds.map(sid=>({job_id:data.id,staff_id:sid})))
        }
      }
    }}
    onRemoveJob={async (jobId) => {
      await supabase.from('jobs').delete().eq('id', jobId)
      syncJobDelete(jobId)
      setJobs(prev => prev.filter(j => j.id !== jobId))
    }}
    onUpdateJob={async (jobId, updates) => {
      const { data } = await supabase.from('jobs').update(updates).eq('id', jobId).select().single()
      if (data) {
        setJobs(prev => prev.map(j => j.id === jobId ? data : j))
        const client = clients.find(c => c.id === data.client_id)
        syncJobUpdate(data, client || {})
      }
      return data
    }}
  />

  // ── MONTH VIEW ─────────────────────────────────────────────────────────────
  if (viewMode === 'mass') {
    return <MassSchedule clients={clients} onBack={()=>setViewMode('week')} />
  }

  if (viewMode === 'month') {
    // Get all days of the month containing the current week
    const refDate = dates[0]
    const year = refDate.getFullYear()
    const month = refDate.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startDow = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1
    const monthLabel = firstDay.toLocaleDateString('es-MX',{month:'long',year:'numeric'})

    // Build calendar grid
    const gridDays = []
    for (let i = 0; i < startDow; i++) gridDays.push(null)
    for (let d = 1; d <= lastDay.getDate(); d++) {
      gridDays.push(new Date(year, month, d))
    }
    while (gridDays.length % 7 !== 0) gridDays.push(null)

    return (
      <div style={s.wrap}>
        <div style={s.topbar}>
          <button style={s.nbtn} onClick={() => {
            const d = new Date(refDate); d.setMonth(d.getMonth()-1)
            setWeekOffset(weekOffset - 4)
          }}>←</button>
          <span style={s.weekLabel}>{monthLabel}</span>
          <button style={s.nbtn} onClick={() => {
            setWeekOffset(weekOffset + 4)
          }}>→</button>
          <div style={{display:'flex',gap:4,marginLeft:'auto'}}>
            <button style={{...s.nbtn,...(viewMode==='week'?{background:'#fdeef3',color:'#c03060',borderColor:'#f4b8cc'}:{})}} onClick={()=>setViewMode('week')}>Semana</button>
            <button style={{...s.nbtn,...(viewMode==='3day'?{background:'#fdeef3',color:'#c03060',borderColor:'#f4b8cc'}:{})}} onClick={()=>{setViewMode('3day');setDayOffset(0)}}>3 días</button>
            <button style={{...s.nbtn,...(viewMode==='month'?{background:'#fdeef3',color:'#c03060',borderColor:'#f4b8cc'}:{})}} onClick={()=>setViewMode('month')}>Mes</button>
          </div>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:12}}>
          {/* Day headers */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:4,marginBottom:4}}>
            {['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'].map(d=>(
              <div key={d} style={{textAlign:'center',fontSize:11,fontWeight:500,color:'#9aa0a6',padding:'4px 0'}}>{d}</div>
            ))}
          </div>
          {/* Calendar grid */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:4}}>
            {gridDays.map((d, i) => {
              if (!d) return <div key={i} style={{minHeight:80}}/>
              const dk = dateKey(d)
              const isToday = dk === dateKey(new Date())
              const dayJobs = jobs.filter(j => j.scheduled_date === dk)
              return (
                <div key={i} style={{
                  minHeight:80, background:'#fff', borderRadius:8,
                  border: isToday ? '2px solid #e8547a' : '1px solid #e3e5e8',
                  padding:'6px',overflow:'hidden'
                }}>
                  <div style={{fontSize:11,fontWeight:isToday?600:400,color:isToday?'#e8547a':'#80868b',marginBottom:4}}>{d.getDate()}</div>
                  {dayJobs.map(job => {
                    const client = clients.find(c=>c.id===job.client_id)
                    const col = COLORS[job.color_idx%COLORS.length]
                    return (
                      <div key={job.id} style={{
                        background:col.bg, borderLeft:`2px solid ${col.border}`,
                        borderRadius:4, padding:'2px 5px', marginBottom:2,
                        fontSize:10, fontWeight:500, color:col.text,
                        whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                        cursor:'pointer'
                      }} onClick={()=>setEditJob({...job})}>
                        {displayName(client)||'?'}
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
        {editJob && (
          <div style={s.overlay} onClick={()=>setEditJob(null)}>
            <div style={s.modal} onClick={e=>e.stopPropagation()}>
              <div style={s.modalTitle}>{clients.find(c=>c.id===editJob.client_id)?.name||'Limpieza'}</div>
              <div style={{fontSize:12,color:'#80868b',marginBottom:12}}>{editJob.start_time} · {editJob.hours}h</div>
              <div style={s.macts}>
                <button style={s.mDel} onClick={()=>removeJob(editJob.id)}>Eliminar</button>
                <button style={s.mCancel} onClick={()=>setEditJob(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── WEEK VIEW ──────────────────────────────────────────────────────────────
  return (
    <div style={s.wrap}>
      <div style={s.topbar}>
        <button style={s.nbtn} onClick={() => {
          if (viewMode==='3day') { if (dayOffset>0) setDayOffset(d=>d-3); else { setWeekOffset(w=>w-1); setDayOffset(4) } }
          else setWeekOffset(w => w-1)
        }}>←</button>
        <span style={s.weekLabel}>{weekLabel}</span>
        <button style={s.nbtn} onClick={() => {
          if (viewMode==='3day') { if (dayOffset+3<7) setDayOffset(d=>d+3); else { setWeekOffset(w=>w+1); setDayOffset(0) } }
          else setWeekOffset(w => w+1)
        }}>→</button>
        <div style={{display:'flex',gap:4,marginLeft:8}}>
          <button style={{...s.nbtn,...(viewMode==='week'?{background:'#fdeef3',color:'#c03060',borderColor:'#f4b8cc'}:{})}} onClick={()=>setViewMode('week')}>Semana</button>
          <button style={{...s.nbtn,...(viewMode==='3day'?{background:'#fdeef3',color:'#c03060',borderColor:'#f4b8cc'}:{})}} onClick={()=>{setViewMode('3day');setDayOffset(0)}}>3 días</button>
          <button style={{...s.nbtn,...(viewMode==='month'?{background:'#fdeef3',color:'#c03060',borderColor:'#f4b8cc'}:{})}} onClick={()=>setViewMode('month')}>Mes</button>
          <button style={{...s.nbtn,...(viewMode==='mass'?{background:'#fdeef3',color:'#c03060',borderColor:'#f4b8cc'}:{})}} onClick={()=>setViewMode('mass')}>📅 Masivo</button>
        </div>
        <button style={s.addCleanBtn} onClick={() => {
          setAddData({ clientId: clients[0]?.id||'', day:'Mon', time:'9:00 AM' })
          setShowAdd(true)
        }}>+ Agregar limpieza</button>
        <button title="Estimación" style={{...s.iconBtn,background:'#7F77DD',color:'#fff',borderColor:'#7F77DD'}} onClick={() => setShowEstimate(true)}>
          📋
        </button>
        {saving && <span style={{fontSize:11,color:'#80868b'}}>Guardando...</span>}
        <button
          title={gcalConnected ? 'Desconectar Google Calendar' : 'Conectar Google Calendar'}
          onClick={async () => {
            if (gcalConnected) {
              if (!window.confirm('¿Desconectar Google Calendar?')) return
              disconnectGoogleCalendar()
              setGcalConnected(false)
            } else {
              try {
                await connectGoogleCalendar()
                setGcalConnected(true)
                const today = new Date().toISOString().slice(0,10)
                const future = new Date(Date.now() + 90*24*60*60*1000).toISOString().slice(0,10)
                // Fetch both fresh so we don't rely on state timing
                const [{ data: upcomingJobs }, { data: freshClients }] = await Promise.all([
                  supabase.from('jobs').select('*').gte('scheduled_date', today).lte('scheduled_date', future).neq('status', 'cancelled'),
                  supabase.from('clients').select('*'),
                ])
                if (upcomingJobs?.length && freshClients?.length) {
                  const { synced, failed, errors } = await bulkSyncJobs(upcomingJobs, freshClients)
                  const msg = `✅ Google Calendar conectado!\n${synced} trabajos sincronizados.${failed > 0 ? `\n\n⚠️ ${failed} fallaron:\n${errors.join('\n')}` : ''}`
                  alert(msg)
                } else {
                  alert('✅ Google Calendar conectado! Los nuevos trabajos se sincronizarán automáticamente.')
                }
              } catch(e) {
                alert('No se pudo conectar: ' + e.message)
              }
            }
          }}
          style={{...s.iconBtn, background: gcalConnected ? '#e8f5e9' : '#fff', color: gcalConnected ? '#2e7d32' : '#80868b', borderColor: gcalConnected ? '#a5d6a7' : '#e3e5e8'}}
        >
          {gcalConnected ? '📅✓' : '📅'}
        </button>
        <button title="Resumen de la semana" style={s.iconBtn} onClick={openWeekSummary}>👥</button>
        <button title="Imprimir reporte de la semana" style={s.iconBtn} onClick={()=>{ openWeekSummary(); setShowPrintReport(true) }}>🖨️</button>
        <button title="Imprimir plan por días (elige cuáles)" style={s.iconBtn} onClick={()=>{
          setPickedDays(new Set(dates.map(dateKey)))
          setShowDayPicker(true)
        }}>🖨️📅</button>
      </div>

      <div style={s.body}>
        {/* SIDEBAR */}
        <div style={s.sidebar}>
          {/* Sidebar tabs */}
          <div style={{display:'flex',gap:3,padding:'6px 8px 0',flexShrink:0}}>
            {[['overdue','Overdue',overdueClients.length,'#f59e0b'],['floating','Floating',floatingClients.length,'#7F77DD']].map(([tab,label,count,color])=>(
              <button key={tab} onClick={()=>setSbTab(tab)}
                style={{flex:1,fontSize:9,padding:'4px 2px',border:'none',borderRadius:6,cursor:'pointer',fontWeight:sbTab===tab?600:400,
                  background:sbTab===tab?color:'#f1f3f4',color:sbTab===tab?'#fff':'#80868b'}}>
                {label}{count>0?` (${count})`:''}
              </button>
            ))}
          </div>
          <div style={s.sbList}>
            {loading ? <div style={s.loading}>Cargando...</div> : (()=>{
              const list = sbTab==='overdue' ? overdueClients : floatingClients
              const chipColor = sbTab==='overdue'?{background:'#fff8e1',borderColor:'#fde68a'}:{background:'#f5f0ff',borderColor:'#d4c5f9'}
              if (list.length===0) return <div style={s.loading}>{sbTab==='overdue'?'Sin overdue ✓':'Sin clientes flotantes'}</div>
              return list.map(c => {
                const pref = c.preferred_day ? DAY_BADGE[c.preferred_day] : null
                return (
                  <div key={c.id} style={{...s.chip,...chipColor,position:'relative'}} draggable
                    onDragStart={e => { setDragging({ type:'sidebar', clientId: c.id }); e.dataTransfer.effectAllowed = 'move' }}>
                    {sbTab==='overdue' && (
                      <button
                        title="Quitar de Overdue"
                        onClick={e => { e.stopPropagation(); dismissOverdue(c) }}
                        style={{position:'absolute',top:3,right:3,width:16,height:16,border:'none',borderRadius:'50%',
                          background:'#fff',color:'#c03060',fontSize:11,lineHeight:'16px',textAlign:'center',cursor:'pointer',padding:0}}>
                        ×
                      </button>
                    )}
                    <div style={s.chipName}>{displayName(c)}{pref && <span style={s.dayBadge}>{pref}</span>}</div>
                    <div style={s.chipSub}>{c.default_hours}h{c.preferred_time ? ` · ${c.preferred_time}` : ''}</div>
                    {c.default_rate && <div style={s.chipRate}>${c.default_rate}</div>}
                  </div>
                )
              })
            })()}
          </div>
          <div style={s.notepadWrap}>
            <div style={s.noteLabel}>Notas de la semana</div>
            <textarea style={s.notepad} value={notepad} placeholder="Apuntes..."
              onChange={e => setNotepad(e.target.value)} />
          </div>
        </div>

        {/* CALENDAR */}
        <div style={s.calWrap}>
          <div style={s.dayHeaders}>
            <div style={s.timeGutter}/>
            {dates.map((date, di) => {
              const isToday = dateKey(date) === dateKey(new Date())
              return (
                <div key={di} style={{...s.dayHeader,...(isToday?s.dayHeaderToday:{}),position:'relative'}}>
                  <span style={s.dayHeaderNum}>{date.getDate()}</span>
                  <span style={s.dayHeaderName}>{DAYS_ES[DAYS[di]]}</span>
                  <button
                    title="Plan del día"
                    onClick={()=>setShowPlan(dateKey(date))}
                    style={{position:'absolute',top:4,right:4,fontSize:10,padding:'2px 6px',border:'1px solid #f4b8cc',borderRadius:6,background:'#fdeef3',color:'#c03060',cursor:'pointer',opacity:.7}}>
                    📋
                  </button>
                </div>
              )
            })}
          </div>

          {/* ALL-DAY ROW */}
          <div style={{display:'flex',borderBottom:'2px solid #e3e5e8',background:'#f8f9fa',flexShrink:0,minHeight:28}}>
            <div style={{...s.timeGutter,fontSize:9,color:'#9aa0a6',display:'flex',alignItems:'center',justifyContent:'center'}}>todo el día</div>
            {dates.map((date, di) => {
              const dk = dateKey(date)
              const dayCode = DAYS[di]
              const offStaff = staff.filter(s => s.availability?.[dayCode] === false).map(s => (s.name||'').split(' ')[0]).filter(Boolean)
              const allDayJobs = jobs.filter(j => j.scheduled_date === dk && !j.start_time && j.status !== 'estimate' && j.status !== 'skipped' && j.status !== 'overdue')
              return (
                <div key={di} style={{flex:1,borderLeft:'1px solid #e3e5e8',padding:'2px 3px',minHeight:28,display:'flex',flexWrap:'wrap',gap:2,alignItems:'flex-start'}}
                  onDragOver={e=>e.preventDefault()}
                  onDrop={e=>{
                    e.preventDefault()
                    if(dragging?.type==='sidebar') dropJobAllDay(date, dragging.clientId)
                    if(dragging?.type==='job') moveJobAllDay(dragging.jobId, date)
                    setDragging(null)
                  }}>
                  {offStaff.length>0 && (
                    <div title={`No trabajan hoy: ${offStaff.join(', ')}`}
                      style={{width:'100%',fontSize:8,fontWeight:700,color:'#c94f4f',opacity:.75,marginBottom:1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                      🚫 {offStaff.join(', ')}
                    </div>
                  )}
                  {allDayJobs.map(job => {
                    const client = clients.find(c=>c.id===job.client_id)
                    const col = COLORS[job.color_idx%COLORS.length]
                    return (
                      <div key={job.id} draggable
                        onDragStart={e=>{setDragging({type:'job',jobId:job.id});e.dataTransfer.effectAllowed='move'}}
                        onClick={()=>setEditJob({...job})}
                        style={{background:col.bg,borderLeft:`2px solid ${col.border}`,borderRadius:3,padding:'1px 5px',fontSize:10,fontWeight:600,color:col.text,cursor:'pointer',whiteSpace:'nowrap',overflow:'hidden',maxWidth:'100%',textOverflow:'ellipsis'}}>
                        {displayName(client)||'?'}{job.not_same_day?' ⛔':''}
                      </div>
                    )
                  })}
                  {/* Ghost blocks for clients due on this day */}
                  {dueClients.filter(dc => {
                    // Show ghost on their preferred day, or spread across week if no preference
                    const freq = FREQ_OPTIONS.find(f=>f.v===dc.frequency)
                    if (!freq?.weeks) return false
                    // Find last job
                    const lastJob = allJobs.filter(j=>j.client_id===dc.id).sort((a,b)=>new Date(b.scheduled_date)-new Date(a.scheduled_date))[0]
                    if (!lastJob) return di===0 // no history, show Monday
                    const nextDue = new Date(lastJob.scheduled_date+'T12:00:00')
                    nextDue.setDate(nextDue.getDate()+freq.weeks*7)
                    return dateKey(nextDue)===dk
                  }).filter(dc => !scheduledClientIds.has(dc.id) && !allDayJobs.find(j=>j.client_id===dc.id)).map(dc=>{
                    const col = COLORS[clients.indexOf(dc)%COLORS.length]
                    const isAirbnb = dc.is_airbnb
                    return (
                      <div key={'ghost-'+dc.id} draggable
                        onDragStart={e=>{setDragging({type:'sidebar',clientId:dc.id});e.dataTransfer.effectAllowed='move'}}
                        title="Arrastra abajo para asignar hora"
                        style={{background:col.bg,borderLeft:`2px dashed ${col.border}`,borderRadius:3,padding:'1px 5px',fontSize:10,fontWeight:500,color:col.text,cursor:'grab',whiteSpace:'nowrap',overflow:'hidden',maxWidth:'100%',textOverflow:'ellipsis',opacity:.75}}>
                        {displayName(dc)}{isAirbnb?' 🏠':''}
                      </div>
                    )
                  })}
                  {allDayJobs.length === 0 && dragging && (
                    <div style={{width:'100%',height:24,border:'1px dashed #e8547a',borderRadius:4,opacity:.4}}/>
                  )}
                </div>
              )
            })}
          </div>
          <div style={s.gridScroll}>
            <div style={{display:'flex',minHeight:TOTAL_SLOTS*SLOT_HEIGHT}}>
              <div style={s.timeCol}>
                {Array.from({length:END_HOUR-START_HOUR},(_,i)=>(
                  <div key={i} style={{...s.timeLabel, top: i*SLOT_HEIGHT*2}}>{fmt12(START_HOUR+i,0)}</div>
                ))}
              </div>
              {dates.map((date, di) => {
                const dayJobs = jobsForDate(date)
                const dk = dateKey(date)
                const isDO = dragOver?.dk === dk
                return (
                  <div key={di} style={{...s.dayCol,...(isDO?s.dayColOver:{})}}
                    onDragOver={e=>{e.preventDefault();const min=getMinFromY(e,e.currentTarget);setDragOver({dk,di,min,date})}}
                    onDragLeave={()=>setDragOver(null)}
                    onDrop={e=>{
                      e.preventDefault()
                      const min=getMinFromY(e,e.currentTarget)
                      if(dragging?.type==='sidebar') dropJob(date,dragging.clientId,min)
                      if(dragging?.type==='job') moveJob(dragging.jobId,date,min)
                      setDragging(null);setDragOver(null)
                    }}>
                    {/* All-day drop zone at very top */}
                    <div style={{position:'absolute',top:0,left:0,right:0,height:28,zIndex:25}}
                      onDragOver={e=>{e.preventDefault();e.stopPropagation()}}
                      onDrop={e=>{
                        e.preventDefault();e.stopPropagation()
                        if(dragging?.type==='sidebar') dropJobAllDay(date,dragging.clientId)
                        if(dragging?.type==='job') moveJobAllDay(dragging.jobId,date)
                        setDragging(null);setDragOver(null)
                      }}/>
                    {Array.from({length:TOTAL_SLOTS},(_,ti)=>(
                      <div key={ti} style={{position:'absolute',left:0,right:0,top:ti*SLOT_HEIGHT,height:SLOT_HEIGHT,borderTop:ti%2===0?'1px solid #e3e5e8':'1px dashed #f8f0ea'}}/>
                    ))}
                    {isDO && dragging && (
                      <div style={{position:'absolute',left:3,right:3,top:minToTop(dragOver.min),height:hrsToHeight(2),background:'rgba(232,84,122,0.15)',border:'2px dashed #e8547a',borderRadius:8,zIndex:5,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,color:'#e8547a',fontWeight:500}}>
                        {minToTimeStr(dragOver.min)}
                      </div>
                    )}
                    {(()=>{
                      const timedJobs = dayJobs.filter(j=>j.start_time&&j.status!=='estimate'&&j.status!=='skipped'&&j.status!=='overdue')
                      const allDayJobs = dayJobs.filter(j=>!j.start_time&&j.status!=='estimate'&&j.status!=='skipped'&&j.status!=='overdue')
                      const {jobCol,jobSpan,totalCols} = computeColumns(dayJobs)
                      const MARGIN = 2
                      return (<>
                        {/* All-day strip */}
                        {allDayJobs.length>0&&(
                          <div style={{position:'absolute',top:0,left:0,right:0,zIndex:20,display:'flex',flexDirection:'column',gap:1,padding:'2px 2px',background:'rgba(255,255,255,0.95)',borderBottom:'1px solid #e3e5e8'}}>
                            {allDayJobs.map(job=>{
                              const client=clients.find(c=>c.id===job.client_id)
                              const col=COLORS[job.color_idx%COLORS.length]
                              return (
                                <div key={job.id} draggable
                                  onDragStart={e=>{setDragging({type:'job',jobId:job.id});e.dataTransfer.effectAllowed='move'}}
                                  onClick={()=>setEditJob({...job})}
                                  style={{background:col.bg,borderLeft:`3px solid ${col.border}`,borderRadius:4,padding:'2px 5px',fontSize:10,fontWeight:600,color:col.text,cursor:'pointer',overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>
                                  {displayName(client)||'?'}{job.default_rate?` · $${job.default_rate}`:''}
                                </div>
                              )
                            })}
                          </div>
                        )}
                        {/* Timed jobs with column split */}
                        {timedJobs.map(job=>{
                          const client=clients.find(c=>c.id===job.client_id)
                          const col=COLORS[job.color_idx%COLORS.length]
                          const conflict=hasConflict(job,dayJobs)
                          const wrongDay=client?.preferred_day&&client.preferred_day!==DAYS[di]
                          const startMin=timeStrToMin(job.start_time||'9:00 AM')-START_HOUR*60
                          const hrs=parseFloat(job.hours)||2
                          const ci = jobCol[job.id]??0
                          const span = jobSpan[job.id]??1
                          const colW = `calc((100% - ${MARGIN*2}px) / ${span})`
                          const colL = `calc(${MARGIN}px + (100% - ${MARGIN*2}px) / ${span} * ${ci})`
                          const isSkipped = job.status === 'skipped'
                          return (
                            <div key={job.id} draggable={!isSkipped}
                              onDragStart={e=>{if(isSkipped)return;setDragging({type:'job',jobId:job.id});e.dataTransfer.effectAllowed='move'}}
                              onClick={()=>setEditJob({...job})}
                              style={{position:'absolute',left:colL,width:colW,top:minToTop(Math.max(0,startMin)),height:Math.max(hrsToHeight(hrs),SLOT_HEIGHT),background:isSkipped?'#f0f0f0':conflict?'#fee2e2':col.bg,borderLeft:`3px solid ${isSkipped?'#bdc1c6':conflict?'#ef4444':col.border}`,borderRadius:7,padding:'4px 7px',cursor:'pointer',zIndex:10,overflow:'hidden',outline:wrongDay&&!conflict&&!isSkipped?'1.5px solid #f97316':'none',opacity:isSkipped?0.5:1}}>
                              <div style={{fontSize:11,fontWeight:600,color:isSkipped?'#80868b':conflict?'#991b1b':col.text,lineHeight:1.2,textDecoration:isSkipped?'line-through':'none'}}>{displayName(client)||'?'}</div>
                              {isSkipped
                                ? <div style={{fontSize:9,color:'#80868b'}}>⏭ Saltado{job.skip_return_date?' · regresa '+new Date(job.skip_return_date+'T12:00:00').toLocaleDateString('es-MX',{month:'short',day:'numeric'}):''}</div>
                                : <>
                                  <div style={{fontSize:10,color:conflict?'#991b1b':col.text,opacity:.8,marginTop:1}}>{job.start_time} · {hrs}h{job.route?` · Ruta ${job.route}`:''}</div>
                                  {job.not_same_day&&<div style={{fontSize:9,fontWeight:700,color:'#fff',background:'#e8547a',borderRadius:3,padding:'1px 5px',marginTop:2,display:'inline-block'}}>⛔ NO SAME-DAY</div>}
                                  {job.one_time_note?<div style={{fontSize:9,color:col.text,opacity:.7,marginTop:1}}>📝 {job.one_time_note}</div>:null}
                                  {conflict&&<div style={{fontSize:9,color:'#991b1b',fontWeight:600,marginTop:2}}>⚠ Conflicto</div>}
                                  {wrongDay&&!conflict&&<div style={{fontSize:9,color:'#c2410c',marginTop:1}}>⚠ Prefiere {DAYS_ES[client.preferred_day]}</div>}
                                </>
                              }
                            </div>
                          )
                        })}
                      </>)
                    })()}

                    {/* Estimate blocks from jobs with type=estimate */}
                    {jobs.filter(j=>j.scheduled_date===dateKey(date)&&j.status==='estimate').map(est=>{
                      const estMin = timeStrToMin(est.start_time||'9:00 AM')-START_HOUR*60
                      return (
                        <div key={est.id} style={{position:'absolute',left:3,right:3,top:minToTop(Math.max(0,estMin)),
                          height:Math.max(hrsToHeight(0.33),SLOT_HEIGHT),
                          background:'#EEEDFE',borderLeft:'3px solid #7F77DD',borderRadius:7,
                          padding:'4px 7px',cursor:'pointer',zIndex:10,overflow:'hidden'}}
                          onClick={()=>setEditJob({...est})}>
                          <div style={{fontSize:10,fontWeight:600,color:'#3C3489'}}>📋 Estimación</div>
                          <div style={{fontSize:9,color:'#3C3489',opacity:.8}}>{est.start_time} · Mami{est.one_time_note?` · ${est.one_time_note}`:''}</div>
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* EDIT MODAL */}
      {editJob && (
        <div style={s.overlay} onClick={()=>setEditJob(null)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.modalTitle}>{clients.find(c=>c.id===editJob.client_id)?.name||'Editar limpieza'}</div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Hora de inicio</label>
              <select style={s.minput} value={editJob.start_time||'9:00 AM'} onChange={e=>setEditJob(j=>({...j,start_time:e.target.value}))}>
                {Array.from({length:TOTAL_SLOTS},(_,i)=>{const min=i*30;const h=START_HOUR+Math.floor(min/60);const m=min%60;const str=fmt12(h,m);return<option key={i} value={str}>{str}</option>})}
              </select>
            </div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Duración (solo esta visita)</label>
              <input style={s.minput} type="number" min="0.5" max="8" step="0.5" value={editJob.hours||2} onChange={e=>setEditJob(j=>({...j,hours:parseFloat(e.target.value)}))}/>
            </div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Ruta</label>
              <select style={s.minput} value={editJob.route||''} onChange={e=>setEditJob(j=>({...j,route:e.target.value}))}>
                <option value="">Sin asignar</option>
                {['A','B','C','D'].map(r=><option key={r} value={r}>Ruta {r}</option>)}
              </select>
            </div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Nota de esta visita (se envía al equipo)</label>
              <input style={s.minput} value={editJob.one_time_note||''} placeholder="Ej: enfocarse en la cocina" onChange={e=>setEditJob(j=>({...j,one_time_note:e.target.value}))}/>
            </div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Dirección para esta visita (opcional — sobreescribe la del cliente)</label>
              <input style={s.minput} value={editJob.one_time_address||''} placeholder="Ej: 123 Oak St, Torrance CA 90501" onChange={e=>setEditJob(j=>({...j,one_time_address:e.target.value}))}/>
            </div>
            {clients.find(c=>c.id===editJob.client_id)?.is_airbnb && (
              <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12,cursor:'pointer',padding:'8px 10px',background:'#faeeda',borderRadius:8,border:'1px solid #fac775',marginBottom:8}}>
                <input type="checkbox"
                  checked={editJob.not_same_day||false}
                  onChange={e=>setEditJob(j=>({...j,not_same_day:e.target.checked}))}/>
                🏠 No es same-day turnover — mostrar <strong>*</strong> en el nombre
              </label>
            )}

            {/* Skip this week */}
            <div style={{padding:'10px 12px',background:editJob.status==='skipped'?'#f1f3f4':'#fff',borderRadius:8,border:'1px solid #e3e5e8',marginBottom:8}}>
              <div style={{fontSize:11,fontWeight:600,color:'#5f6368',marginBottom:6}}>⏭ Saltar esta semana</div>
              {editJob.status==='skipped' ? (
                <div>
                  <div style={{fontSize:11,color:'#80868b',marginBottom:6}}>Este cliente está marcado como saltado esta semana.</div>
                  <div style={{display:'flex',gap:6,alignItems:'center',marginBottom:6}}>
                    <span style={{fontSize:11,color:'#5f6368'}}>Regresa:</span>
                    <input type="date" style={{fontSize:11,padding:'3px 6px',border:'1px solid #ddd',borderRadius:6}}
                      value={editJob.skip_return_date||''}
                      onChange={e=>setEditJob(j=>({...j,skip_return_date:e.target.value}))}/>
                  </div>
                  <button style={{fontSize:11,padding:'4px 10px',border:'1px solid #1d9e75',borderRadius:6,background:'#e1f5ee',color:'#085041',cursor:'pointer'}}
                    onClick={()=>setEditJob(j=>({...j,status:'scheduled',skip_return_date:null}))}>
                    ✓ Restaurar a programado
                  </button>
                </div>
              ) : (
                <div>
                  <div style={{fontSize:11,color:'#80868b',marginBottom:6}}>Marcar si el cliente pidió saltar esta semana.</div>
                  <div style={{display:'flex',gap:6,alignItems:'center',marginBottom:6}}>
                    <span style={{fontSize:11,color:'#5f6368'}}>Regresa el:</span>
                    <input type="date" style={{fontSize:11,padding:'3px 6px',border:'1px solid #ddd',borderRadius:6}}
                      value={editJob.skip_return_date||''}
                      onChange={e=>setEditJob(j=>({...j,skip_return_date:e.target.value}))}/>
                  </div>
                  <button style={{fontSize:11,padding:'4px 10px',border:'1px solid #80868b',borderRadius:6,background:'#f1f3f4',color:'#5f6368',cursor:'pointer'}}
                    onClick={()=>skipJob(editJob, editJob.skip_return_date)}>
                    ⏭ Marcar como saltado
                  </button>
                  <button style={{fontSize:11,padding:'4px 10px',border:'1px solid #f59e0b',borderRadius:6,background:'#fff8e1',color:'#92400e',cursor:'pointer',marginTop:6}}
                    onClick={()=>moveToOverdue(editJob)}>
                    ⚠️ Mover a Overdue
                  </button>
                </div>
              )}
            </div>
            <div style={s.macts}>
              <button style={s.mDel} onClick={()=>removeJob(editJob.id)}>Eliminar</button>
              <button style={s.mCancel} onClick={()=>setEditJob(null)}>Cancelar</button>
              <button style={s.mSave} onClick={saveEdit} disabled={saving}>{saving?'Guardando...':'Guardar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ADD MODAL */}
      {showWeekSummary && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200}} onClick={()=>setShowWeekSummary(false)}>
          <div style={{background:'#fff',borderRadius:16,boxShadow:'0 16px 48px rgba(0,0,0,0.2)',width:420,maxWidth:'92vw',maxHeight:'80vh',overflow:'hidden',display:'flex',flexDirection:'column'}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 18px',borderBottom:'1px solid #e3e5e8'}}>
              <div style={{fontSize:15,fontWeight:700}}>👥 Resumen de la semana</div>
              <button style={{fontSize:20,border:'none',background:'none',cursor:'pointer',color:'#9aa0a6'}} onClick={()=>setShowWeekSummary(false)}>×</button>
            </div>
            <div style={{overflowY:'auto',padding:'14px 18px'}}>
              {!weekSummaryData ? (
                <div style={{textAlign:'center',color:'#9aa0a6',padding:24}}>Cargando...</div>
              ) : weekSummaryData.length === 0 ? (
                <div style={{textAlign:'center',color:'#9aa0a6',padding:24}}>Sin rutas publicadas esta semana</div>
              ) : weekSummaryData.map(s => {
                const DAY_SHORT = {Mon:'L',Tue:'M',Wed:'Mi',Thu:'J',Fri:'V',Sat:'S',Sun:'D'}
                const dayEntries = Object.entries(s.days).sort(([a],[b])=>a.localeCompare(b))
                return (
                  <div key={s.name} style={{marginBottom:12,padding:'10px 14px',border:'1px solid #e3e5e8',borderRadius:10,background:'#f8f9fa'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                      <div style={{fontWeight:600,fontSize:13}}>{s.name}</div>
                      <div style={{fontSize:12,fontWeight:500,color:'#e8547a'}}>{s.totalHours.toFixed(1)} hrs</div>
                    </div>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      {dayEntries.map(([date,hrs])=>{
                        const d = new Date(date+'T12:00:00')
                        const dayName = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'][d.getDay()]
                        return (
                          <div key={date} style={{fontSize:11,padding:'2px 8px',borderRadius:99,background:'#7F77DD',color:'#fff',fontWeight:500}}>
                            {dayName} {d.getDate()} · {hrs.toFixed(1)}h
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {showPrintReport && (
        <WeekPrintReport
          dates={dates}
          jobs={jobs}
          clients={clients}
          staff={staff}
          routeStaffMap={routeStaffMap}
          weekSummaryData={weekSummaryData}
          timesheetRows={timesheetRows}
          onClose={()=>setShowPrintReport(false)}
        />
      )}

      {showDayPicker && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:350}} onClick={()=>setShowDayPicker(false)}>
          <div style={{background:'#fff',borderRadius:14,padding:22,width:320}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>¿Qué días quieres imprimir?</div>
            <div style={{fontSize:12,color:'#80868b',marginBottom:14}}>Elige uno o varios días de esta semana</div>
            {dates.map((d,i) => {
              const dk = dateKey(d)
              const checked = pickedDays.has(dk)
              return (
                <label key={dk} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 4px',cursor:'pointer',fontSize:14}}>
                  <input type="checkbox" checked={checked} onChange={()=>{
                    setPickedDays(prev => {
                      const next = new Set(prev)
                      if (next.has(dk)) next.delete(dk); else next.add(dk)
                      return next
                    })
                  }} />
                  {DAYS_ES[DAYS[i]]} {d.getDate()}
                </label>
              )
            })}
            <div style={{display:'flex',gap:8,marginTop:16,justifyContent:'flex-end'}}>
              <button style={{padding:'8px 14px',border:'1px solid #e3e5e8',borderRadius:8,background:'#fff',color:'#5f6368',cursor:'pointer',fontSize:12}}
                onClick={()=>setShowDayPicker(false)}>Cancelar</button>
              <button style={{padding:'8px 16px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontWeight:600,fontSize:12,cursor: pickedDays.size?'pointer':'not-allowed',opacity:pickedDays.size?1:0.5}}
                disabled={!pickedDays.size}
                onClick={async ()=>{
                  await openWeekSummary()
                  setShowDayPicker(false)
                  setShowMultiDayPrint(true)
                }}>Generar</button>
            </div>
          </div>
        </div>
      )}

      {showMultiDayPrint && (
        <PlanMultiDayPrint
          dates={dates.filter(d=>pickedDays.has(dateKey(d)))}
          jobs={jobs}
          clients={clients}
          staff={staff}
          routeStaffMap={routeStaffMap}
          onClose={()=>setShowMultiDayPrint(false)}
        />
      )}

      {showPlan && (
        <PlanDelDia
          date={showPlan}
          clients={clients}
          staff={staff}
          onClose={()=>setShowPlan(null)}
          onSaved={()=>{ setShowPlan(null); fetchWeek() }}
        />
      )}

      {showAdd && (
        <div style={s.overlay} onClick={()=>setShowAdd(false)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.modalTitle}>Agregar limpieza</div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Cliente</label>
              <div style={{display:'flex',gap:6,marginBottom:6}}>
                <input style={{...s.minput,flex:1}} placeholder="Buscar cliente..." autoFocus
                  value={addData.search||''} onChange={e=>setAddData(d=>({...d,search:e.target.value,clientId:''}))}/>
              </div>
              {(addData.search||'').length > 0 && (
                <div style={{border:'1px solid #e3e5e8',borderRadius:8,maxHeight:160,overflowY:'auto',background:'#fff',marginBottom:8}}>
                  {clients.filter(c=>c.name.toLowerCase().includes((addData.search||'').toLowerCase())||((c.nickname||'').toLowerCase().includes((addData.search||'').toLowerCase()))).map(c=>(
                    <div key={c.id} style={{padding:'8px 12px',fontSize:12,cursor:'pointer',background:addData.clientId===c.id?'#fdeef3':'#fff',borderBottom:'1px solid #f8f9fa'}}
                      onClick={()=>setAddData(d=>({...d,clientId:c.id,search:c.nickname||c.name}))}>
                      {c.nickname||c.name}
                    </div>
                  ))}
                  {clients.filter(c=>c.name.toLowerCase().includes((addData.search||'').toLowerCase())).length===0 && (
                    <div style={{padding:'8px 12px'}}>
                      <div style={{fontSize:12,color:'#9aa0a6',marginBottom:6}}>Sin resultados</div>
                      <button style={{fontSize:11,padding:'5px 10px',border:'none',borderRadius:6,background:'#e8547a',color:'#fff',cursor:'pointer'}}
                        onClick={()=>{ setShowAdd(false); onNavigate&&onNavigate('clients') }}>
                        + Agregar nuevo cliente
                      </button>
                    </div>
                  )}
                </div>
              )}
              {addData.clientId && (
                <div style={{fontSize:11,color:'#1d9e75',marginBottom:6}}>✓ {clients.find(c=>c.id===addData.clientId)?.name}</div>
              )}
            </div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Día</label>
              <select style={s.minput} value={addData.day} onChange={e=>setAddData(d=>({...d,day:e.target.value}))}>
                {DAYS.map(d=><option key={d} value={d}>{DAYS_ES[d]}</option>)}
              </select>
            </div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Hora</label>
              <select style={s.minput} value={addData.time} onChange={e=>setAddData(d=>({...d,time:e.target.value}))}>
                {Array.from({length:TOTAL_SLOTS},(_,i)=>{const min=i*30;const h=START_HOUR+Math.floor(min/60);const m=min%60;const str=fmt12(h,m);return<option key={i} value={str}>{str}</option>})}
              </select>
            </div>
            <div style={s.macts}>
              <button style={s.mCancel} onClick={()=>setShowAdd(false)}>Cancelar</button>
              <button style={s.mSave} onClick={addCleaning} disabled={saving}>{saving?'Guardando...':'Agregar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ESTIMATE MODAL */}
      {showEstimate && (
        <div style={s.overlay} onClick={()=>setShowEstimate(false)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.modalTitle}>📋 Agregar estimación</div>
            <div style={{fontSize:12,color:'#9aa0a6',marginBottom:12}}>Siempre asignada a Mami · ~20 min</div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Dirección del cliente</label>
              <input style={s.minput} value={estimateData.address} placeholder="123 Oak St, Gardena"
                onChange={e=>setEstimateData(d=>({...d,address:e.target.value}))}/>
            </div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Día</label>
              <select style={s.minput} value={estimateData.day} onChange={e=>setEstimateData(d=>({...d,day:e.target.value}))}>
                {DAYS.map(d=><option key={d} value={d}>{DAYS_ES[d]}</option>)}
              </select>
            </div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Hora</label>
              <select style={s.minput} value={estimateData.time} onChange={e=>setEstimateData(d=>({...d,time:e.target.value}))}>
                {Array.from({length:TOTAL_SLOTS},(_,i)=>{const min=i*30;const h=START_HOUR+Math.floor(min/60);const m=min%60;const str=fmt12(h,m);return<option key={i} value={str}>{str}</option>})}
              </select>
            </div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Notas</label>
              <input style={s.minput} value={estimateData.notes} placeholder="Nombre del cliente, notas..."
                onChange={e=>setEstimateData(d=>({...d,notes:e.target.value}))}/>
            </div>
            <div style={s.macts}>
              <button style={s.mCancel} onClick={()=>setShowEstimate(false)}>Cancelar</button>
              <button style={{...s.mSave,background:'#7F77DD'}} disabled={saving} onClick={async()=>{
                const di = DAYS.indexOf(estimateData.day)
                if (di<0) return
                setSaving(true)
                const { data } = await supabase.from('jobs').insert([{
                  client_id: null,
                  scheduled_date: dateKey(dates[di]),
                  start_time: estimateData.time,
                  hours: 0.33,
                  status: 'estimate',
                  route: '',
                  one_time_note: estimateData.address + (estimateData.notes?` — ${estimateData.notes}`:''),
                  week_key: wk, color_idx: 4,
                }]).select().single()
                if (data) setJobs(prev=>[...prev,data])
                setShowEstimate(false)
                setSaving(false)
              }}>
                Agregar estimación
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── MASS SCHEDULE VIEW ──────────────────────────────────────────────────────
// ── MOBILE SCHEDULE — Google Calendar style ───────────────────────────────────
const HOUR_H = 56 // px per hour
const HOURS = Array.from({length:24},(_,i)=>i)
const COLORS_MOB = ['#4285f4','#0f9d58','#ab47bc','#f4b400','#e8547a','#00acc1','#ff7043']

function minToTimeStr12(min) {
  const h = Math.floor(min/60)
  const m = min%60
  const ampm = h>=12?'PM':'AM'
  const h12 = h>12?h-12:h===0?12:h
  return `${h12}:${String(m).padStart(2,'0')} ${ampm}`
}

function MobileSchedule({ clients, jobs, allJobs, dates, weekOffset, setWeekOffset, loading, staff, wk, onAddJob, onRemoveJob, onUpdateJob }) {
  const todayStr = dateKey(new Date())
  const [selDate, setSelDate] = useState(dates.find(d=>dateKey(d)===todayStr) ? todayStr : dateKey(dates[0]))
  const [view, setView] = useState('day') // day | month | due
  const [showAddSheet, setShowAddSheet] = useState(false)
  const [editJob, setEditJob] = useState(null)
  const [addClientId, setAddClientId] = useState('')
  const [addQuery, setAddQuery] = useState('')
  const [addTime, setAddTime] = useState('09:00')
  const [addHours, setAddHours] = useState('2')
  const [addRoute, setAddRoute] = useState('A')
  const [addStaff, setAddStaff] = useState([])
  const [addNote, setAddNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [dragJob, setDragJob] = useState(null)
  const [dragY, setDragY] = useState(0)
  const [dragRoute, setDragRoute] = useState(null)
  const [jobStaffMap, setJobStaffMap] = useState({})
  const [monthOffset, setMonthOffset] = useState(0)
  const scrollRef = useRef(null)
  const dragStateRef = useRef({ startY:0, startMin:0, currentDelta:0, job:null, startX:0 })
  const gridRef = useRef(null)

  const PINK = '#e8547a'
  const BORDER = '#e3e5e8'
  const TEAMS = ['A','B','C']
  const TEAM_COLORS = { A:'#3b82f6', B:'#22c55e', C:'#a78bfa' }
  const TEAM_BG = { A:'#eff6ff', B:'#f0fdf4', C:'#f5f3ff' }

  // Inject CSS
  useEffect(() => {
    const existing = document.getElementById('mob-gcal-css')
    if (existing) existing.remove()
    const el = document.createElement('style')
    el.id = 'mob-gcal-css'
    el.textContent = `
      *{-webkit-tap-highlight-color:transparent}
      .mgc-page{height:100vh;display:flex;flex-direction:column;background:#fff;font-family:'DM Sans',Arial,sans-serif;overflow:hidden}
      .mgc-topbar{display:flex;align-items:center;padding:5px 10px;background:#fff;border-bottom:1px solid #e8eaed;flex-shrink:0;gap:4px}
      .mgc-daystrip{display:flex;overflow-x:auto;gap:1px;padding:4px 8px;background:#fff;border-bottom:1px solid #e8eaed;scrollbar-width:none;flex-shrink:0}
      .mgc-daystrip::-webkit-scrollbar{display:none}
      .mgc-daybtn{flex-shrink:0;width:40px;padding:3px 1px;border-radius:9px;border:none;cursor:pointer;text-align:center;background:transparent}
      .mgc-dayname{font-size:9px;font-weight:700;text-transform:uppercase;color:#9aa0a6;margin-bottom:1px}
      .mgc-daynum{width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:#202124;margin:0 auto;border-radius:50%}
      .mgc-daybtn.active .mgc-daynum{background:#e8547a;color:#fff}
      .mgc-daybtn.today:not(.active) .mgc-daynum{border:2px solid #e8547a}
      .mgc-dotrow{height:4px;display:flex;justify-content:center;gap:2px;margin-top:2px}
      .mgc-dot{width:3px;height:3px;border-radius:50%}
      .mgc-notime{padding:4px 4px 4px 48px;background:#f8f9fa;border-bottom:1px solid #e8eaed;flex-shrink:0}
      .mgc-chip{display:inline-flex;align-items:center;gap:3px;padding:3px 8px;border-radius:5px;font-size:11px;font-weight:600;color:#fff;margin:1px;cursor:pointer}
      .mgc-grid{flex:1;overflow-y:auto;position:relative}
      .mgc-gridinner{position:relative;display:flex}
      .mgc-timecol{width:44px;flex-shrink:0;position:relative}
      .mgc-teamcols{flex:1;display:grid;grid-template-columns:1fr 1fr 1fr;position:relative}
      .mgc-teamcol{position:relative;border-left:1px solid #e8eaed;min-height:100%}
      .mgc-teamhdr{position:sticky;top:0;z-index:10;text-align:center;padding:5px 2px;font-size:11px;font-weight:700;border-bottom:2px solid;background:#fff}
      .mgc-hrlabel{position:absolute;right:3px;font-size:9px;color:#9aa0a6;transform:translateY(-50%);pointer-events:none;white-space:nowrap}
      .mgc-hrline{position:absolute;left:0;right:0;border-top:1px solid #e8eaed;pointer-events:none}
      .mgc-halfline{position:absolute;left:0;right:0;border-top:1px dashed #f1f3f4;pointer-events:none}
      .mgc-event{position:absolute;left:2px;right:2px;border-radius:6px;padding:4px 5px;cursor:pointer;overflow:hidden;box-sizing:border-box;touch-action:none;user-select:none;-webkit-user-select:none;border-left:3px solid}
      .mgc-fab{position:fixed;bottom:18px;right:14px;width:48px;height:48px;border-radius:50%;background:#e8547a;border:none;color:#fff;font-size:22px;cursor:pointer;box-shadow:0 4px 16px rgba(232,84,122,.45);z-index:50;display:flex;align-items:center;justify-content:center}
      .sheet-ov{position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:200;display:flex;align-items:flex-end}
      .sheet{background:#fff;border-radius:22px 22px 0 0;width:100%;padding:14px 15px 32px;max-height:93vh;overflow-y:auto}
      .sheet-handle{width:34px;height:4px;background:#e0e0e0;border-radius:99px;margin:0 auto 13px}
      .mob-inp{width:100%;padding:9px 12px;border:1.5px solid #e3e5e8;border-radius:10px;font-size:14px;background:#f8f9fa;font-family:inherit;outline:none;box-sizing:border-box}
      .mob-inp:focus{border-color:#e8547a}
      .mob-btn{width:100%;padding:12px;border-radius:12px;border:none;font-size:14px;font-weight:700;cursor:pointer;font-family:inherit;margin-top:6px}
      .mob-row{display:flex;gap:7px;margin-bottom:10px}
      .mob-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#9aa0a6;margin-bottom:3px}
      .mob-viewbtn{padding:5px 11px;border-radius:7px;border:none;font-size:11px;font-weight:600;cursor:pointer;font-family:inherit}
      .staff-chip{display:inline-flex;align-items:center;padding:4px 10px;border-radius:99px;font-size:11px;font-weight:600;cursor:pointer;margin:2px;border:1.5px solid;transition:all .12s}
      .due-card{background:#fff;border:1px solid #e3e5e8;border-radius:11px;padding:11px 13px;margin-bottom:7px;display:flex;justify-content:space-between;align-items:center}
      .team-tab{flex:1;padding:6px 4px;border:none;border-bottom:2.5px solid transparent;font-size:12px;font-weight:700;cursor:pointer;font-family:inherit;background:transparent;transition:all .12s}
      .team-tab.active{border-bottom-color:currentColor}
      .client-row{padding:10px 12px;font-size:13px;border-bottom:1px solid #e3e5e8;cursor:pointer;display:flex;align-items:center;gap:8px}
      .client-row:active{background:#f8f9fa}
    `
    document.head.appendChild(el)
  }, [])

  useEffect(() => {
    if (scrollRef.current && view==='day') scrollRef.current.scrollTop = 7 * HOUR_H
  }, [view, selDate])

  useEffect(() => {
    const jobIds = jobs.map(j=>j.id)
    if (!jobIds.length) return
    supabase.from('job_staff').select('job_id,staff_id').in('job_id', jobIds).then(({data}) => {
      if (!data) return
      const map = {}
      data.forEach(r => { if (!map[r.job_id]) map[r.job_id]=[]; map[r.job_id].push(r.staff_id) })
      setJobStaffMap(map)
    })
  }, [jobs])

  async function toggleJobStaff(jobId, staffId) {
    const current = jobStaffMap[jobId]||[]
    if (current.includes(staffId)) {
      await supabase.from('job_staff').delete().eq('job_id',jobId).eq('staff_id',staffId)
      setJobStaffMap(m=>({...m,[jobId]:(m[jobId]||[]).filter(id=>id!==staffId)}))
    } else {
      await supabase.from('job_staff').insert({job_id:jobId,staff_id:staffId})
      setJobStaffMap(m=>({...m,[jobId]:[...(m[jobId]||[]),staffId]}))
    }
  }

  async function completeJob(job) {
    await onUpdateJob(job.id,{status:'completed'})
    const staffNames=(jobStaffMap[job.id]||[]).map(sid=>staff.find(s=>s.id===sid)?.name).filter(Boolean).join(', ')
    await supabase.from('cleaning_history').insert({
      job_id:job.id, client_id:job.client_id,
      cleaned_date:job.scheduled_date,
      hours_actual:parseFloat(job.hours)||2,
      staff_names:staffNames||null,
    })
    setEditJob(null)
  }

  // ── Touch drag: vertical time + horizontal route ───────────────────────────
  function onEventTouchStart(e, job) {
    e.stopPropagation()
    const startY=e.touches[0].clientY, startX=e.touches[0].clientX
    const startMin=timeStrToMin(job.start_time)||0
    dragStateRef.current={startY,startX,startMin,currentDelta:0,routeDelta:0,job}
    setDragJob(job); setDragY(0); setDragRoute(job.route||'A')

    function onMove(ev) {
      ev.preventDefault()
      const dy=ev.touches[0].clientY-dragStateRef.current.startY
      const dx=ev.touches[0].clientX-dragStateRef.current.startX
      const dMin=Math.round((dy/HOUR_H)*60/15)*15
      dragStateRef.current.currentDelta=dMin

      // Determine which column based on x position
      if (gridRef.current) {
        const rect=gridRef.current.getBoundingClientRect()
        const relX=ev.touches[0].clientX-rect.left
        const colW=rect.width/3
        const colIdx=Math.max(0,Math.min(2,Math.floor(relX/colW)))
        const newRoute=TEAMS[colIdx]
        setDragRoute(newRoute)
        dragStateRef.current.routeDelta=newRoute
      }
      setDragY(dMin)
    }
    async function onEnd() {
      const {startMin,currentDelta,job,routeDelta}=dragStateRef.current
      const snapped=Math.round(Math.max(0,Math.min(23*60,startMin+currentDelta))/15)*15
      const newRoute=routeDelta||job.route||'A'
      await onUpdateJob(job.id,{start_time:minToTimeStr12(snapped),route:newRoute})
      setDragJob(null); setDragY(0); setDragRoute(null)
      dragStateRef.current={startY:0,startX:0,startMin:0,currentDelta:0,routeDelta:null,job:null}
      document.removeEventListener('touchmove',onMove)
      document.removeEventListener('touchend',onEnd)
    }
    document.addEventListener('touchmove',onMove,{passive:false})
    document.addEventListener('touchend',onEnd)
  }

  // ── Due clients ─────────────────────────────────────────────────────────────
  const scheduledClientIds=new Set(jobs.map(j=>j.client_id))
  const eligible=clients.filter(c=>c.is_active&&!c.is_realtor&&!c.is_airbnb&&!c.is_commercial)
  const dueClients=eligible.filter(c=>!scheduledClientIds.has(c.id)&&isClientDueThisWeek(c,dates,allJobs))
  const overdueClients=eligible.filter(c=>{
    if(scheduledClientIds.has(c.id)||dueClients.find(d=>d.id===c.id)) return false
    if(['custom','one-time'].includes(c.frequency)) return false
    return recurringStatus(c, allJobs, dates) === 'overdue'
  })

  // ── Selected day data ──────────────────────────────────────────────────────
  const selJobs=jobs.filter(j=>j.scheduled_date===selDate)
  const noTimeJobs=selJobs.filter(j=>!j.start_time)
  const filteredClients=addQuery?clients.filter(c=>c.name.toLowerCase().includes(addQuery.toLowerCase())):clients

  // ── Month helpers ──────────────────────────────────────────────────────────
  const monthRef=new Date(new Date().getFullYear(),new Date().getMonth()+monthOffset,1)
  const firstDow=monthRef.getDay()
  const daysInMonth=new Date(monthRef.getFullYear(),monthRef.getMonth()+1,0).getDate()

  function openAddSheet(route, timeMin) {
    setAddRoute(route||'A')
    setAddTime(timeMin!=null?`${String(Math.floor(timeMin/60)).padStart(2,'0')}:${String(timeMin%60).padStart(2,'0')}`:'09:00')
    setAddClientId(''); setAddQuery(''); setAddHours('2'); setAddStaff([]); setAddNote('')
    setShowAddSheet(true)
  }

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="mgc-page">

      {/* ── Top bar ── */}
      <div className="mgc-topbar">
        {[['day','📅'],['month','🗓'],['due','⚠️']].map(([v,icon])=>(
          <button key={v} className="mob-viewbtn"
            style={{background:view===v?PINK:'transparent',color:view===v?'#fff':'#80868b'}}>
            <span onClick={()=>setView(v)}>{icon} {v==='day'?'Día':v==='month'?'Mes':'Pendientes'}</span>
          </button>
        ))}
        <div style={{marginLeft:'auto',display:'flex',gap:3}}>
          <button onClick={()=>view==='month'?setMonthOffset(o=>o-1):setWeekOffset(w=>w-1)}
            style={{background:'none',border:'1px solid #e8eaed',borderRadius:7,padding:'4px 8px',cursor:'pointer',fontSize:13,color:'#5f6368'}}>‹</button>
          <button onClick={()=>view==='month'?setMonthOffset(o=>o+1):setWeekOffset(w=>w+1)}
            style={{background:'none',border:'1px solid #e8eaed',borderRadius:7,padding:'4px 8px',cursor:'pointer',fontSize:13,color:'#5f6368'}}>›</button>
        </div>
      </div>

      {/* ══════ DAY VIEW ══════ */}
      {view==='day'&&<>
        {/* Day strip */}
        <div className="mgc-daystrip">
          {dates.map(d=>{
            const str=dateKey(d),active=str===selDate,isToday=str===todayStr
            const cnt=jobs.filter(j=>j.scheduled_date===str).length
            return (
              <button key={str} className={`mgc-daybtn${active?' active':''}${isToday?' today':''}`}
                onClick={()=>setSelDate(str)}>
                <div className="mgc-dayname">{d.toLocaleDateString('es-MX',{weekday:'short'}).toUpperCase()}</div>
                <div className="mgc-daynum">{d.getDate()}</div>
                <div className="mgc-dotrow">{cnt>0&&[...Array(Math.min(cnt,5))].map((_,i)=>(
                  <div key={i} className="mgc-dot" style={{background:active?'rgba(255,255,255,.8)':PINK}}/>
                ))}</div>
              </button>
            )
          })}
        </div>

        {/* No-time strip */}
        {noTimeJobs.length>0&&(
          <div className="mgc-notime">
            <div style={{fontSize:9,color:'#80868b',fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:3}}>📋 Sin hora</div>
            {noTimeJobs.map(job=>{
              const cl=clients.find(c=>c.id===job.client_id)
              const route=job.route||'?'
              const color=TEAM_COLORS[route]||'#80868b'
              const assigned=(jobStaffMap[job.id]||[]).map(sid=>staff.find(s=>s.id===sid)?.name?.split(' ')[0]).filter(Boolean)
              return (
                <span key={job.id} className="mgc-chip"
                  style={{background:color,border:`2px solid ${color}`}}>
                  {route} · {cl?.name||'?'}{assigned.length?` (${assigned.join(', ')})` : ''}
                  <span onTouchEnd={e=>{e.stopPropagation();setEditJob({...job})}}
                    onClick={e=>{e.stopPropagation();setEditJob({...job})}}
                    style={{opacity:.8,fontSize:12}}>✏️</span>
                </span>
              )
            })}
          </div>
        )}

        {/* 3-column time grid */}
        <div className="mgc-grid" ref={scrollRef}>
          <div style={{display:'flex',flexDirection:'column',minHeight:24*HOUR_H+32}}>

            {/* Team headers — sticky */}
            <div style={{display:'flex',flexShrink:0,background:'#fff',borderBottom:'1px solid #e8eaed',position:'sticky',top:0,zIndex:20}}>
              <div style={{width:44,flexShrink:0}}/>
              {TEAMS.map(t=>(
                <div key={t} className="mgc-teamhdr"
                  style={{flex:1,color:TEAM_COLORS[t],borderBottomColor:TEAM_COLORS[t],background:TEAM_BG[t]}}>
                  Equipo {t}
                  <div style={{fontSize:9,fontWeight:400,color:'#80868b',marginTop:1}}>
                    {selJobs.filter(j=>j.route===t&&j.start_time).length} limpieza{selJobs.filter(j=>j.route===t&&j.start_time).length!==1?'s':''}
                  </div>
                </div>
              ))}
            </div>

            {/* Grid body */}
            <div className="mgc-gridinner" style={{flex:1,height:24*HOUR_H}}>

              {/* Time labels */}
              <div className="mgc-timecol" style={{height:24*HOUR_H,position:'absolute',left:0,top:0}}>
                {HOURS.map(h=>(
                  <div key={h} className="mgc-hrlabel" style={{top:h*HOUR_H}}>
                    {h===0?'':h<12?`${h}AM`:h===12?'12PM':`${h-12}PM`}
                  </div>
                ))}
              </div>

              {/* 3 team columns */}
              <div ref={gridRef} style={{position:'absolute',left:44,right:0,top:0,height:24*HOUR_H,display:'grid',gridTemplateColumns:'1fr 1fr 1fr'}}>
                {TEAMS.map(team=>{
                  const teamJobs=selJobs.filter(j=>j.route===team&&j.start_time)
                  return (
                    <div key={team} className="mgc-teamcol"
                      style={{background:dragRoute===team&&dragJob?`${TEAM_BG[team]}cc`:'transparent'}}>

                      {/* Hour lines */}
                      {HOURS.map(h=>(
                        <React.Fragment key={h}>
                          <div className="mgc-hrline" style={{top:h*HOUR_H}}/>
                          <div className="mgc-halfline" style={{top:h*HOUR_H+HOUR_H/2}}/>
                        </React.Fragment>
                      ))}

                      {/* Hour lines only - no tap slots */}

                      {/* Job events */}
                      {teamJobs.map(job=>{
                        const cl=clients.find(c=>c.id===job.client_id)
                        const min=timeStrToMin(job.start_time)
                        const hours=parseFloat(job.hours)||1
                        const isDragging=dragJob?.id===job.id
                        const displayMin=isDragging?Math.max(0,Math.min(23*60,min+dragY)):min
                        const top=(displayMin/60)*HOUR_H
                        const height=Math.max(28,hours*HOUR_H-3)
                        const color=TEAM_COLORS[team]
                        const assigned=(jobStaffMap[job.id]||[]).map(sid=>staff.find(s=>s.id===sid)?.name?.split(' ')[0]).filter(Boolean)
                        const isCompleted=job.status==='completed'

                        return (
                          <div key={job.id} className="mgc-event"
                            style={{
                              top, height,
                              background:isCompleted?'#f0f0f0':`${color}18`,
                              borderLeftColor:isCompleted?'#bdc1c6':color,
                              opacity:isDragging?.65:isCompleted?.7:1,
                              boxShadow:isDragging?`0 4px 20px ${color}44`:'none',
                              zIndex:isDragging?30:2,
                            }}
                            onTouchStart={e=>onEventTouchStart(e,job)}>
                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                              <div style={{fontSize:10,fontWeight:700,color:isCompleted?'#80868b':color,lineHeight:1.2,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                                {isCompleted?'✅ ':''}{cl?.name||'?'}
                              </div>
                              <span
                                onTouchEnd={e=>{e.stopPropagation();if(!dragJob)setEditJob({...job})}}
                                onClick={e=>{e.stopPropagation();setEditJob({...job})}}
                                style={{fontSize:10,flexShrink:0,marginLeft:2,opacity:.7,cursor:'pointer'}}>✏️</span>
                            </div>
                            {height>34&&(
                              <div style={{fontSize:9,color:isCompleted?'#9aa0a6':'#5f6368',marginTop:1,lineHeight:1.3}}>
                                {job.start_time} · {hours}h
                                {assigned.length>0&&<><br/>{assigned.join(', ')}</>}
                              </div>
                            )}
                            {height>56&&cl?.address&&(
                              <div style={{fontSize:9,color:'#80868b',marginTop:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                                📍 {cl.address}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>

              {/* Current time line */}
              {selDate===todayStr&&(()=>{
                const n=new Date(),min=n.getHours()*60+n.getMinutes()
                return (
                  <div style={{position:'absolute',top:(min/60)*HOUR_H,left:38,right:0,height:2,background:PINK,zIndex:25,pointerEvents:'none'}}>
                    <div style={{position:'absolute',left:-4,top:-4,width:8,height:8,borderRadius:'50%',background:PINK}}/>
                  </div>
                )
              })()}
            </div>
          </div>
        </div>

        {/* FAB */}
        <button className="mgc-fab" onClick={()=>openAddSheet('A',null)}>+</button>
      </>}

      {/* ══════ MONTH VIEW ══════ */}
      {view==='month'&&(
        <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
          <div style={{textAlign:'center',padding:'7px 0',fontSize:13,fontWeight:700,color:'#202124',borderBottom:'1px solid #e8eaed',textTransform:'capitalize'}}>
            {monthRef.toLocaleDateString('es-MX',{month:'long',year:'numeric'})}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',background:'#e8eaed',gap:'1px',flexShrink:0}}>
            {['D','L','M','M','J','V','S'].map((d,i)=>(
              <div key={i} style={{background:'#f8f9fa',textAlign:'center',fontSize:9,fontWeight:700,color:'#9aa0a6',padding:'3px 0'}}>{d}</div>
            ))}
          </div>
          <div style={{flex:1,overflowY:'auto'}}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:'1px',background:'#e8eaed'}}>
              {Array.from({length:firstDow}).map((_,i)=><div key={`e${i}`} style={{background:'#f8f9fa',minHeight:56}}/>)}
              {Array.from({length:daysInMonth}).map((_,i)=>{
                const d=i+1
                const str=`${monthRef.getFullYear()}-${String(monthRef.getMonth()+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`
                const isToday=str===todayStr,isSel=str===selDate
                const dayJobs=jobs.filter(j=>j.scheduled_date===str)
                return (
                  <div key={d} style={{background:isSel?'#fdeef3':isToday?'#fff5f8':'#fff',minHeight:56,padding:2,cursor:'pointer'}}
                    onClick={()=>{setSelDate(str);setView('day')}}>
                    <div style={{fontSize:11,fontWeight:isToday?800:600,color:isToday?PINK:'#5f6368',marginBottom:1}}>{d}</div>
                    {TEAMS.map(t=>{
                      const tjobs=dayJobs.filter(j=>j.route===t)
                      if(!tjobs.length) return null
                      return <div key={t} style={{fontSize:8,color:'#fff',background:TEAM_COLORS[t],borderRadius:2,padding:'1px 3px',marginBottom:1,overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>
                        {t}: {tjobs.map(j=>clients.find(c=>c.id===j.client_id)?.name?.split(' ')[0]||'?').join(', ')}
                      </div>
                    })}
                    {dayJobs.filter(j=>!j.route).map(job=>{
                      const cl=clients.find(c=>c.id===job.client_id)
                      return <div key={job.id} style={{fontSize:8,color:'#fff',background:'#80868b',borderRadius:2,padding:'1px 3px',marginBottom:1,overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>
                        {cl?.name?.split(' ')[0]||'?'}
                      </div>
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* ══════ DUE / PENDING VIEW ══════ */}
      {view==='due'&&(
        <div style={{flex:1,overflowY:'auto',padding:'10px 12px'}}>
          {dueClients.length===0&&overdueClients.length===0?(
            <div style={{textAlign:'center',padding:'48px 0',color:'#9aa0a6',fontSize:13}}>
              <div style={{fontSize:40,marginBottom:12}}>✅</div>
              Todo está al día esta semana
            </div>
          ):null}

          {overdueClients.length>0&&<>
            <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.07em',color:'#e24b4a',marginBottom:7}}>⚠️ Vencidos</div>
            {overdueClients.map(c=>(
              <div key={c.id} className="due-card" style={{borderLeft:`3px solid #e24b4a`}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.name}</div>
                  <div style={{fontSize:10,color:'#9aa0a6',marginTop:1}}>{FREQ_OPTIONS.find(f=>f.v===c.frequency)?.l||c.frequency}</div>
                  {c.address&&<div style={{fontSize:10,color:'#80868b',marginTop:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>📍 {c.address}</div>}
                </div>
                <button style={{background:PINK,border:'none',borderRadius:8,color:'#fff',fontSize:11,fontWeight:700,padding:'6px 10px',cursor:'pointer',marginLeft:8,flexShrink:0}}
                  onClick={()=>{setAddClientId(c.id);setAddQuery(c.name);openAddSheet('A',null);setView('day')}}>
                  + Agendar
                </button>
              </div>
            ))}
          </>}

          {dueClients.length>0&&<>
            <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.07em',color:PINK,marginBottom:7,marginTop:overdueClients.length?14:0}}>📅 Esta semana</div>
            {dueClients.map(c=>(
              <div key={c.id} className="due-card">
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.name}</div>
                  <div style={{fontSize:10,color:'#9aa0a6',marginTop:1}}>{FREQ_OPTIONS.find(f=>f.v===c.frequency)?.l||c.frequency}</div>
                  {c.preferred_day&&<div style={{fontSize:10,color:'#80868b',marginTop:1}}>📌 Pref: {c.preferred_day}</div>}
                  {c.address&&<div style={{fontSize:10,color:'#80868b',marginTop:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>📍 {c.address}</div>}
                </div>
                <button style={{background:PINK,border:'none',borderRadius:8,color:'#fff',fontSize:11,fontWeight:700,padding:'6px 10px',cursor:'pointer',marginLeft:8,flexShrink:0}}
                  onClick={()=>{setAddClientId(c.id);setAddQuery(c.name);openAddSheet('A',null);setView('day')}}>
                  + Agendar
                </button>
              </div>
            ))}
          </>}
        </div>
      )}

      {/* ══════ ADD SHEET ══════ */}
      {showAddSheet&&(
        <div className="sheet-ov" onClick={()=>setShowAddSheet(false)}>
          <div className="sheet" onClick={e=>e.stopPropagation()}>
            <div className="sheet-handle"/>
            <div style={{fontSize:15,fontWeight:700,marginBottom:12}}>
              ➕ {new Date(selDate+'T12:00:00').toLocaleDateString('es-MX',{weekday:'long',month:'long',day:'numeric'})}
            </div>

            {/* Team tabs */}
            <div style={{display:'flex',borderBottom:'1px solid #e3e5e8',marginBottom:12}}>
              {TEAMS.map(t=>(
                <button key={t} className={`team-tab${addRoute===t?' active':''}`}
                  style={{color:TEAM_COLORS[t]}}
                  onClick={()=>setAddRoute(t)}>
                  Equipo {t}
                </button>
              ))}
            </div>

            {/* Client search */}
            <div style={{marginBottom:10}}>
              <div className="mob-label">Cliente</div>
              <input className="mob-inp" placeholder="Buscar..." value={addQuery}
                onChange={e=>{setAddQuery(e.target.value);if(!e.target.value)setAddClientId('')}}
                autoFocus={false}/>
              {addQuery&&!addClientId&&filteredClients.length>0&&(
                <div style={{background:'#fff',border:`1.5px solid ${BORDER}`,borderRadius:10,marginTop:3,maxHeight:130,overflowY:'auto',position:'relative',zIndex:10}}>
                  {filteredClients.slice(0,8).map(c=>(
                    <div key={c.id} className="client-row"
                      onClick={()=>{setAddClientId(c.id);setAddQuery(c.name)}}>
                      <div style={{width:28,height:28,borderRadius:'50%',background:TEAM_COLORS[addRoute],color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,flexShrink:0}}>
                        {c.name[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{fontWeight:500}}>{c.name}</div>
                        {c.address&&<div style={{fontSize:11,color:'#9aa0a6'}}>{c.address}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {addClientId&&(
                <div style={{marginTop:4,padding:'6px 10px',background:TEAM_BG[addRoute]||'#f1f3f4',borderRadius:8,fontSize:12,color:TEAM_COLORS[addRoute],fontWeight:600,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  ✓ {addQuery}
                  <span style={{cursor:'pointer',color:'#9aa0a6'}} onClick={()=>{setAddClientId('');setAddQuery('')}}>✕</span>
                </div>
              )}
            </div>

            <div className="mob-row">
              <div style={{flex:1}}>
                <div className="mob-label">Hora</div>
                <input className="mob-inp" type="time" value={addTime} onChange={e=>setAddTime(e.target.value)}/>
              </div>
              <div style={{flex:1}}>
                <div className="mob-label">Horas</div>
                <input className="mob-inp" type="number" step="0.5" min="0.5" max="12"
                  value={addHours} onChange={e=>setAddHours(e.target.value)}/>
              </div>
            </div>

            <div style={{marginBottom:10}}>
              <div className="mob-label">Nota (opcional)</div>
              <input className="mob-inp" placeholder="Ej: enfocarse en cocina..."
                value={addNote} onChange={e=>setAddNote(e.target.value)}/>
            </div>

            {/* Staff picker */}
            {staff.length>0&&(
              <div style={{marginBottom:12}}>
                <div className="mob-label">Equipo asignado</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:3}}>
                  {staff.map(s=>{
                    const sel=addStaff.includes(s.id)
                    return (
                      <button key={s.id} className="staff-chip"
                        style={{background:sel?s.color||PINK:'#fff',color:sel?'#fff':'#5f6368',borderColor:s.color||BORDER}}
                        onClick={()=>setAddStaff(prev=>sel?prev.filter(id=>id!==s.id):[...prev,s.id])}>
                        {sel?'✓ ':''}{s.name.split(' ')[0]}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            <button className="mob-btn"
              style={{background:addClientId?TEAM_COLORS[addRoute]||PINK:'#e0e0e0',color:'#fff'}}
              disabled={!addClientId||saving}
              onClick={async()=>{
                setSaving(true)
                const [h,m]=addTime.split(':').map(Number)
                const timeStr=minToTimeStr12(h*60+m)
                await onAddJob(addClientId, new Date(selDate+'T12:00:00'), timeStr, parseFloat(addHours)||2, addRoute, addStaff, addNote)
                setSaving(false); setShowAddSheet(false)
              }}>
              {saving?'Guardando...':`✅ Guardar — Equipo ${addRoute}`}
            </button>
            <button className="mob-btn" style={{background:'#f1f3f4',color:'#80868b'}} onClick={()=>setShowAddSheet(false)}>Cancelar</button>
          </div>
        </div>
      )}

      {/* ══════ EDIT SHEET ══════ */}
      {editJob&&(()=>{
        const cl=clients.find(c=>c.id===editJob.client_id)
        const route=editJob.route||'A'
        const color=TEAM_COLORS[route]||PINK
        const assignedStaff=jobStaffMap[editJob.id]||[]
        return (
          <div className="sheet-ov" onClick={()=>setEditJob(null)}>
            <div className="sheet" onClick={e=>e.stopPropagation()}>
              <div className="sheet-handle"/>

              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
                <div style={{width:8,height:44,borderRadius:99,background:color,flexShrink:0}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:17,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{cl?.name}</div>
                  {cl?.address&&<div style={{fontSize:11,color:'#80868b',marginTop:1}}>📍 {cl.address}{cl.city?`, ${cl.city}`:''}</div>}
                </div>
                <div style={{background:TEAM_BG[route],color,fontSize:11,fontWeight:700,padding:'4px 10px',borderRadius:99,flexShrink:0}}>
                  Equipo {route}
                </div>
              </div>

              {cl?.entry_code&&(
                <div style={{background:'#f0fdf4',border:'1px solid #a5d6a7',borderRadius:10,padding:'9px 13px',marginBottom:11}}>
                  <div style={{fontSize:9,color:'#1d9e75',fontWeight:700,textTransform:'uppercase',marginBottom:2}}>Código de entrada</div>
                  <div style={{fontSize:22,fontWeight:700,letterSpacing:'3px',color:'#1a5c2a'}}>{cl.entry_code}</div>
                </div>
              )}

              {cl?.notes&&(
                <div style={{background:'#f8f9fa',borderRadius:10,padding:'8px 12px',marginBottom:10,fontSize:12,color:'#5f6368',lineHeight:1.6}}>
                  📝 {cl.notes}
                </div>
              )}

              {cl?.guest_prefs&&(
                <div style={{background:'#fff3f7',borderRadius:10,padding:'8px 12px',marginBottom:10,fontSize:12,color:'#5f6368',lineHeight:1.6}}>
                  🏠 {cl.guest_prefs}
                </div>
              )}

              {/* Route selector */}
              <div style={{marginBottom:10}}>
                <div className="mob-label">Equipo</div>
                <div style={{display:'flex',gap:6}}>
                  {TEAMS.map(t=>(
                    <button key={t}
                      style={{flex:1,padding:'8px',border:`2px solid ${editJob.route===t?TEAM_COLORS[t]:BORDER}`,borderRadius:10,background:editJob.route===t?TEAM_BG[t]:'#fff',color:editJob.route===t?TEAM_COLORS[t]:'#80868b',fontWeight:700,fontSize:13,cursor:'pointer'}}
                      onClick={()=>setEditJob(j=>({...j,route:t}))}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mob-row">
                <div style={{flex:1}}>
                  <div className="mob-label">Hora inicio</div>
                  <input className="mob-inp" type="time"
                    value={editJob.start_time?(()=>{const m=timeStrToMin(editJob.start_time);return `${String(Math.floor(m/60)).padStart(2,'0')}:${String(m%60).padStart(2,'0')}`})():''}
                    onChange={e=>{
                      if(!e.target.value){setEditJob(j=>({...j,start_time:''}));return}
                      const [h,m]=e.target.value.split(':').map(Number)
                      setEditJob(j=>({...j,start_time:minToTimeStr12(h*60+m)}))
                    }}/>
                </div>
                <div style={{flex:1}}>
                  <div className="mob-label">Horas</div>
                  <input className="mob-inp" type="number" step="0.5" min="0.5" max="12"
                    value={editJob.hours||''}
                    onChange={e=>setEditJob(j=>({...j,hours:e.target.value}))}/>
                </div>
              </div>

              <div className="mob-row">
                <div style={{flex:1}}>
                  <div className="mob-label">Estado</div>
                  <select className="mob-inp" value={editJob.status||'scheduled'}
                    onChange={e=>setEditJob(j=>({...j,status:e.target.value}))}>
                    <option value="scheduled">Programado</option>
                    <option value="in_progress">En progreso</option>
                    <option value="completed">Completado ✅</option>
                    <option value="cancelled">Cancelado</option>
                  </select>
                </div>
              </div>

              <div style={{marginBottom:11}}>
                <div className="mob-label">Nota</div>
                <input className="mob-inp" placeholder="Nota para este servicio..."
                  value={editJob.one_time_note||''}
                  onChange={e=>setEditJob(j=>({...j,one_time_note:e.target.value}))}/>
              </div>

              {staff.length>0&&(
                <div style={{marginBottom:12}}>
                  <div className="mob-label">Personal asignado</div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:3}}>
                    {staff.map(s=>{
                      const assigned=assignedStaff.includes(s.id)
                      return (
                        <button key={s.id} className="staff-chip"
                          style={{background:assigned?s.color||PINK:'#fff',color:assigned?'#fff':'#5f6368',borderColor:s.color||BORDER}}
                          onClick={()=>toggleJobStaff(editJob.id,s.id)}>
                          {assigned?'✓ ':''}{s.name.split(' ')[0]}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              <button className="mob-btn" style={{background:color,color:'#fff'}} disabled={saving}
                onClick={async()=>{
                  setSaving(true)
                  await onUpdateJob(editJob.id,{
                    start_time:editJob.start_time||null,
                    hours:parseFloat(editJob.hours)||2,
                    route:editJob.route||'A',
                    status:editJob.status,
                    one_time_note:editJob.one_time_note||'',
                  })
                  setSaving(false); setEditJob(null)
                }}>
                {saving?'Guardando...':'💾 Guardar cambios'}
              </button>

              {editJob.status!=='completed'&&(
                <button className="mob-btn" style={{background:'#f0fdf4',color:'#1d9e75'}}
                  onClick={()=>completeJob(editJob)}>
                  ✅ Marcar completado + historial
                </button>
              )}

              <button className="mob-btn" style={{background:'#fff0f3',color:PINK}}
                onClick={async()=>{
                  if(!window.confirm('¿Eliminar esta limpieza?')) return
                  await onRemoveJob(editJob.id); setEditJob(null)
                }}>🗑 Eliminar</button>

              <button className="mob-btn" style={{background:'#f1f3f4',color:'#80868b'}} onClick={()=>setEditJob(null)}>Cerrar</button>
            </div>
          </div>
        )
      })()}
    </div>
  )
}

export function MassSchedule({ clients, onBack }) {
  const [selectedClient, setSelectedClient] = useState('')
  const [clientQuery, setClientQuery] = useState('')
  const [clientListOpen, setClientListOpen] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date(); return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`
  })
  const [selectedDays, setSelectedDays] = useState(new Set())
  const [useTime, setUseTime] = useState(false)
  const [time, setTime] = useState('9:00 AM')
  const [notSameDay, setNotSameDay] = useState(false) // Airbnb * toggle
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)

  const [year, month] = selectedMonth.split('-').map(Number)
  const firstDay = new Date(year, month-1, 1)
  const lastDay = new Date(year, month, 0)
  const startDow = firstDay.getDay() // Sunday-first, to match Hostaway
  const MONTHS_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']

  const gridDays = []
  for (let i=0;i<startDow;i++) gridDays.push(null)
  for (let d=1;d<=lastDay.getDate();d++) gridDays.push(new Date(year,month-1,d))

  const selectedClientObj = clients.find(c=>c.id===selectedClient)
  const isAirbnb = selectedClientObj?.is_airbnb

  function toggleDay(d) {
    const dk = d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0')
    setSelectedDays(prev => { const s=new Set(prev); s.has(dk)?s.delete(dk):s.add(dk); return s })
  }

  async function createAll() {
    if (!selectedClient || selectedDays.size===0) return
    const client = clients.find(c=>c.id===selectedClient)
    const noteSuffix = notSameDay ? ' *' : ''
    setSaving(true)
    const inserts = [...selectedDays].sort().map((dk,i) => ({
      client_id: selectedClient,
      scheduled_date: dk,
      start_time: useTime ? time : null,
      hours: client?.default_hours||2,
      status: 'scheduled',
      route: '', 
      one_time_note: notSameDay ? '' : '',
      week_key: `w-mass`,
      color_idx: i%5,
    }))
    await supabase.from('jobs').insert(inserts)
    setSaving(false)
    setDone(true)
    setSelectedDays(new Set())
    setNotSameDay(false)
  }

  return (
    <div style={{padding:'20px 24px',overflowY:'auto',height:'100%'}}>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:20}}>
        <button style={{fontSize:13,border:'1px solid #e3e5e8',borderRadius:8,padding:'5px 12px',background:'#fff',cursor:'pointer',color:'#5f6368'}} onClick={onBack}>← Volver</button>
        <div style={{fontSize:18,fontWeight:600}}>📅 Programación masiva</div>
      </div>
      {done && <div style={{padding:'10px 14px',background:'#e1f5ee',borderRadius:8,fontSize:13,color:'#085041',marginBottom:16}}>✓ Limpiezas creadas exitosamente</div>}
      <div style={{display:'grid',gridTemplateColumns:'280px 1fr',gap:20}}>
        <div>
          <div style={{fontSize:11,color:'#80868b',marginBottom:4}}>Cliente</div>
          {selectedClientObj ? (
            <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',border:'1px solid #e8547a',borderRadius:8,background:'#fdeef3',marginBottom:12}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:'#202124',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{selectedClientObj.name}</div>
                {selectedClientObj.nickname && selectedClientObj.nickname!==selectedClientObj.name && <div style={{fontSize:11,color:'#80868b'}}>{selectedClientObj.nickname}</div>}
              </div>
              <button onClick={()=>{ setSelectedClient(''); setClientQuery(''); setClientListOpen(true) }}
                style={{border:'none',background:'none',color:'#80868b',fontSize:12,cursor:'pointer',padding:0}}>Cambiar</button>
            </div>
          ) : (
            <div style={{position:'relative',marginBottom:12}}>
              <input autoFocus placeholder="Buscar cliente..." value={clientQuery}
                onChange={e=>{ setClientQuery(e.target.value); setClientListOpen(true) }}
                onFocus={()=>setClientListOpen(true)}
                onBlur={()=>setTimeout(()=>setClientListOpen(false),150)}
                onKeyDown={e=>{
                  if (e.key!=='Enter') return
                  const q=clientQuery.trim().toLowerCase()
                  const first=[...clients].filter(c=>!q||(c.name||'').toLowerCase().includes(q)||(c.nickname||'').toLowerCase().includes(q)||(c.address||'').toLowerCase().includes(q))
                    .sort((a,b)=>(a.name||'').localeCompare(b.name||''))[0]
                  if (first) { setSelectedClient(first.id); setClientListOpen(false) }
                }}
                style={{width:'100%',padding:'8px 10px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:13,background:'#fff',boxSizing:'border-box'}}/>
              {clientListOpen && (()=>{
                const q=clientQuery.trim().toLowerCase()
                const matches=[...clients]
                  .filter(c=>!q||(c.name||'').toLowerCase().includes(q)||(c.nickname||'').toLowerCase().includes(q)||(c.address||'').toLowerCase().includes(q))
                  .sort((a,b)=>(a.name||'').localeCompare(b.name||''))
                return (
                  <div style={{position:'absolute',top:'100%',left:0,right:0,marginTop:4,maxHeight:280,overflowY:'auto',background:'#fff',border:'1px solid #e3e5e8',borderRadius:8,boxShadow:'0 4px 16px rgba(60,64,67,.15)',zIndex:20}}>
                    {matches.length===0
                      ? <div style={{padding:'10px 12px',fontSize:12,color:'#9aa0a6'}}>Sin resultados</div>
                      : matches.map(c=>(
                          <div key={c.id} onMouseDown={e=>{ e.preventDefault(); setSelectedClient(c.id); setClientListOpen(false) }}
                            style={{padding:'8px 12px',cursor:'pointer',borderBottom:'1px solid #f1f3f4'}}
                            onMouseEnter={e=>e.currentTarget.style.background='#f8f9fa'} onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                            <div style={{fontSize:13,color:'#202124'}}>{c.name}{c.nickname&&c.nickname!==c.name?<span style={{color:'#80868b'}}> · {c.nickname}</span>:null}</div>
                            {c.address && <div style={{fontSize:11,color:'#9aa0a6'}}>{c.address}</div>}
                          </div>
                        ))}
                  </div>
                )
              })()}
            </div>
          )}
          <div style={{fontSize:11,color:'#80868b',marginBottom:4}}>Mes</div>
          <input type="month" style={{width:'100%',padding:'8px 10px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:13,background:'#f8f9fa',marginBottom:12}}
            value={selectedMonth} onChange={e=>setSelectedMonth(e.target.value)}/>

          {/* Time toggle */}
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:'#5f6368',marginBottom:8,cursor:'pointer'}}>
            <input type="checkbox" checked={useTime} onChange={e=>setUseTime(e.target.checked)}/>
            Asignar hora específica
          </label>
          {useTime && (
            <select style={{width:'100%',padding:'8px 10px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:13,background:'#f8f9fa',marginBottom:12}}
              value={time} onChange={e=>setTime(e.target.value)}>
              {Array.from({length:24},(_,i)=>{const h=7+Math.floor(i/2);const m=i%2===0?0:30;const str=fmt12(h,m);return<option key={i} value={str}>{str}</option>})}
            </select>
          )}

          {/* Airbnb * toggle */}
          {isAirbnb && (
            <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12,cursor:'pointer',padding:'8px 10px',background:'#faeeda',borderRadius:8,border:'1px solid #fac775',marginBottom:12}}>
              <input type="checkbox" checked={notSameDay} onChange={e=>setNotSameDay(e.target.checked)}/>
              <span>🏠 No es same-day turnover <strong>(*)</strong></span>
            </label>
          )}

          <div style={{fontSize:12,color:'#9aa0a6',marginBottom:8}}>{selectedDays.size} días seleccionados · {useTime?'con hora':'todo el día'}</div>
          <button style={{width:'100%',padding:'10px',border:'none',borderRadius:10,background:selectedClient&&selectedDays.size>0?'#e8547a':'#e3e5e8',color:'#fff',fontWeight:600,cursor:selectedClient&&selectedDays.size>0?'pointer':'not-allowed',fontSize:13}}
            onClick={createAll} disabled={saving||!selectedClient||selectedDays.size===0}>
            {saving?'Creando...':`✓ Crear ${selectedDays.size} limpiezas`}
          </button>
          <button style={{width:'100%',padding:'8px',border:'1px solid #e3e5e8',borderRadius:8,background:'#fff',color:'#80868b',cursor:'pointer',fontSize:12,marginTop:8}}
            onClick={()=>setSelectedDays(new Set())}>Limpiar selección</button>
        </div>
        <div>
          <div style={{fontSize:14,fontWeight:600,marginBottom:10}}>{MONTHS_ES[month-1]} {year}</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:4,marginBottom:6}}>
            {['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'].map(d=>(
              <div key={d} style={{textAlign:'center',fontSize:11,color:'#9aa0a6',padding:'4px 0'}}>{d}</div>
            ))}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:4}}>
            {gridDays.map((d,i)=>{
              if (!d) return <div key={i}/>
              const dk = d.toISOString().slice(0,10)
              const isSelected = selectedDays.has(dk)
                            return (
                <div key={i} onClick={()=>toggleDay(d)}
                  style={{textAlign:'center',padding:'10px 4px',borderRadius:8,cursor:'pointer',
                    background:isSelected?'#e8547a':'#fff',
                    color:isSelected?'#fff':'#202124',
                    border:`1px solid ${isSelected?'#e8547a':'#e3e5e8'}`,
                    fontSize:13,fontWeight:isSelected?600:400}}>
                  {d.getDate()}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

const s = {
  wrap:{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'},
  topbar:{display:'flex',alignItems:'center',gap:8,padding:'9px 14px',borderBottom:'1px solid #e3e5e8',background:'#fff',flexShrink:0},
  nbtn:{padding:'4px 10px',border:'1px solid #e3e5e8',borderRadius:8,background:'#f8f9fa',cursor:'pointer',fontSize:14,color:'#5f6368'},
  iconBtn:{width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',border:'1px solid #e3e5e8',borderRadius:8,background:'#f8f9fa',cursor:'pointer',fontSize:15,padding:0,flexShrink:0},
  weekLabel:{fontSize:13,fontWeight:500,color:'#202124',flex:1,textAlign:'center'},
  addCleanBtn:{fontSize:12,padding:'5px 12px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontWeight:500,cursor:'pointer'},
  body:{display:'flex',flex:1,overflow:'hidden'},
  sidebar:{width:175,borderRight:'1px solid #e3e5e8',display:'flex',flexDirection:'column',background:'#f8f9fa',flexShrink:0,overflow:'hidden'},
  sbHead:{padding:'10px 12px 6px',borderBottom:'1px solid #e3e5e8',flexShrink:0},
  sbTitle:{fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'.05em',color:'#9aa0a6'},
  sbSub:{fontSize:10,color:'#bdc1c6',marginTop:1},
  sbList:{flex:1,overflowY:'auto',padding:7},
  loading:{fontSize:11,color:'#9aa0a6',textAlign:'center',padding:12},
  chip:{background:'#fff',border:'1px solid #e3e5e8',borderRadius:8,padding:'7px 9px',marginBottom:5,cursor:'grab',userSelect:'none'},
  chipName:{fontSize:12,fontWeight:500,color:'#202124',display:'flex',alignItems:'center',gap:5},
  chipSub:{fontSize:10,color:'#9aa0a6',marginTop:2},
  chipRate:{fontSize:10,color:'#1d9e75',fontWeight:500,marginTop:2},
  dayBadge:{fontSize:9,fontWeight:600,background:'#e6f1fb',color:'#0c447c',borderRadius:3,padding:'1px 4px'},
  notepadWrap:{padding:8,borderTop:'1px solid #e3e5e8',flexShrink:0},
  noteLabel:{fontSize:10,color:'#9aa0a6',marginBottom:3},
  notepad:{width:'100%',height:140,fontSize:12,resize:'vertical',border:'1px solid #e3e5e8',borderRadius:7,padding:'7px 9px',background:'#fff',color:'#333',fontFamily:'inherit'},
  calWrap:{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'},
  dayHeaders:{display:'flex',borderBottom:'1px solid #e3e5e8',background:'#fff',flexShrink:0},
  timeGutter:{width:52,flexShrink:0},
  dayHeader:{flex:1,padding:'8px 6px',textAlign:'center',borderLeft:'1px solid #e3e5e8'},
  dayHeaderToday:{background:'#fdeef3'},
  dayHeaderNum:{display:'block',fontSize:18,fontWeight:600,color:'#202124',lineHeight:1.1},
  dayHeaderName:{fontSize:10,color:'#80868b',fontWeight:500},
  gridScroll:{flex:1,overflowY:'auto',position:'relative'},
  timeCol:{width:52,flexShrink:0,position:'relative',borderRight:'1px solid #e3e5e8'},
  timeLabel:{position:'absolute',right:6,fontSize:9,color:'#9aa0a6',lineHeight:1,transform:'translateY(-50%)'},
  dayCol:{flex:1,borderLeft:'1px solid #e3e5e8',position:'relative',minHeight:'100%'},
  dayColOver:{background:'rgba(232,84,122,0.03)'},
  overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.3)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100},
  modal:{background:'#fff',borderRadius:14,padding:'20px 22px',width:320,boxShadow:'0 8px 32px rgba(0,0,0,0.15)'},
  modalTitle:{fontSize:15,fontWeight:600,color:'#202124',marginBottom:14},
  mfield:{marginBottom:12},
  mlabel:{fontSize:11,color:'#80868b',display:'block',marginBottom:3},
  minput:{width:'100%',padding:'7px 9px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:13,background:'#f8f9fa',color:'#202124'},
  macts:{display:'flex',gap:6,marginTop:16},
  mDel:{fontSize:12,padding:'7px 12px',border:'1px solid #e3e5e8',borderRadius:8,background:'#fff',color:'#e24b4a',cursor:'pointer'},
  mCancel:{flex:1,fontSize:12,padding:'7px',border:'1px solid #e3e5e8',borderRadius:8,background:'#fff',color:'#80868b',cursor:'pointer'},
  mSave:{flex:1,fontSize:12,padding:'7px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontWeight:500,cursor:'pointer'},
}
